// Per-business content for the /ph/why-this-matters sales page.
// Each entry personalizes the risk-framing example and reputation proof
// using only real data already collected for that business's concept site.
import { CLINICS } from "../dental/dentalClinics";
import { EVENT_BUSINESSES } from "../events/eventBusinesses";

export const BUSINESS_HOOKS = {
  covo: {
    name: "COVO 24K GOLD HK",
    conceptPath: "/covo-24k-gold-hk",
    hero: "/covo/hero-jewelry.jpg",
    risk: "You're about to spend ₱50,000–₱300,000+ on 24K gold.",
    reputation: { rating: null, reviewCount: 82, stat: "96% recommended", statLabel: "on Facebook, from 82 public reviews" },
    opening: "COVO 24K GOLD HK already has something a website cannot manufacture: 96% of its 82 public Facebook reviews recommend it, for a specific specialty — 24K Hong Kong-style gold.",
    presence: { facebook: "weak", instagram: "weak", website: "none", extra: "The Facebook page is a numeric profile ID rather than a named page, and Ongpin has many gold sellers with similar positioning. A small Instagram account (@covo_jewelry, roughly 126 followers) also exists." },
    findings: [
      "A Facebook page, though under a numeric profile link rather than a clean business handle",
      "96% recommend rate from 82 public reviews",
      "Publicly documented on Ongpin since 2019",
      "Specializes specifically in 24K Hong Kong-style gold — Piyao charms, custom bracelets, chains, rings",
      "A small, lightly-followed Instagram account, and no independent website",
    ],
    problem: "24K Hong Kong-style gold is a specific, distinctive specialty on a street full of general gold sellers — but a numeric Facebook link doesn't reinforce that specialty, and doesn't clearly separate COVO from every other Ongpin gold shop a search might surface.",
    fixes: [
      "The 24K Hong Kong-gold specialty stated clearly, up front",
      "Piyao charms, custom bracelets and chains organized so they can be browsed",
      "The 96%-recommend, 82-review reputation presented as COVO's own",
      "A clear, findable identity separate from other Ongpin sellers",
    ],
    closing: "82 people already recommend COVO's gold. A website makes sure they're recommending a page people can actually find again.",
  },
  "helen-jewelry-shop-concept": {
    name: "Helen Jewelry Shop",
    conceptPath: "/helen-jewelry-shop-concept",
    hero: "/helen/hero.jpg",
    risk: "You're about to spend ₱50,000–₱300,000+ on an engagement ring.",
    reputation: { rating: "4.6", reviewCount: 260 },
    opening: "Helen Jewelry Shop already has something a website cannot manufacture: 70 years in business, a 4.6★ rating across 260 reviews, and a second showroom in Mandaluyong.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "A domain (helenjewelryshop.com) exists but currently shows only a \"coming soon\" placeholder, not a live site. 927 Ongpin St. sits among several similarly-branded gold sellers along the same street." },
    findings: [
      "An active Facebook page",
      "4.6★ across 260 reviews",
      "70 years in business, publicly described as one of the most trusted shops on Ongpin",
      "Two locations — 927 Ongpin St., Manila, and a showroom at SM Cherry Shaw, Shaw Blvd., Mandaluyong City",
      "A domain exists (helenjewelryshop.com) but currently shows only a \"coming soon\" placeholder",
      "No Instagram account",
    ],
    problem: "70 years and two locations is a rare, strong story — but there's already an unfinished \"coming soon\" page sitting at Helen's own domain, which is worse than having no website at all: it signals a project that was started and abandoned.",
    fixes: [
      "The 70-year history told as Helen's own story",
      "Both the Ongpin and Mandaluyong locations shown clearly",
      "Engagement rings and fine jewelry categories organized clearly",
      "260 reviews presented as Helen's own, verifiable reputation",
    ],
    closing: "70 years and 260 reviews are real proof. A finished website replaces the unfinished \"coming soon\" page currently sitting at Helen's own domain.",
  },
  "ty-po-huat-goldsmith-concept": {
    name: "Ty Po Huat Goldsmith",
    conceptPath: "/ty-po-huat-goldsmith-concept",
    hero: "/typohuat/hero.jpg",
    risk: "You're about to commission a custom wedding piece worth ₱50,000 or more.",
    reputation: { rating: "4.7", reviewCount: 268 },
    opening: "Ty Po Huat Goldsmith already has something a website cannot manufacture: 76 years on Ongpin, since 1949, a 4.7★ rating across 268 reviews, and a genuinely active Instagram following.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "An active Instagram account (@typohuatgoldsmith) has roughly 4,600 followers." },
    findings: [
      "An active Facebook page and a well-followed Instagram (@typohuatgoldsmith, ~4,600 followers)",
      "4.7★ across 268 reviews",
      "Family goldsmithing on Ongpin since 1949 — 76 years",
      "Custom goldsmithing, repair and wedding-jewelry work passed down through the family",
      "No independent website — a Setmore booking page handles consultation scheduling",
    ],
    problem: "76 years of family goldsmithing is an exceptional, rare credential — but it currently lives only in social posts, not presented anywhere as the multi-generational story it actually is.",
    fixes: [
      "The 1949 founding and the family's 76-year history told as Ty Po Huat's own story",
      "Custom wedding-jewelry commissioning explained clearly",
      "268 reviews presented as proof of decades of trusted work",
      "A direct way to start a custom commission",
    ],
    closing: "76 years and 268 reviews are a rare combination. A website is where that heritage finally gets the presentation it deserves.",
  },
  "mayfair-goldsmith-concept": {
    name: "Mayfair Goldsmith",
    conceptPath: "/mayfair-goldsmith-concept",
    hero: "/mayfair/logo-card.jpg",
    risk: "You're about to buy gold priced by weight — real money, decided in a few minutes.",
    reputation: { rating: "4.3", reviewCount: 44 },
    opening: "Mayfair Goldsmith already has something a website cannot manufacture: a 4.3★ rating across 44 reviews and a genuinely large Instagram following of over 9,000, all at 810 Ongpin St.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "Instagram (@mayfair.goldsmith) has roughly 9,100 followers and over 1,600 posts — a real, active following. \"Mayfair\" alone can still produce unrelated search results, and a similarly-named account (@mayfair_goldsmiths) also exists, so the Ongpin address matters for disambiguation." },
    findings: [
      "An active Facebook page and a large, active Instagram (@mayfair.goldsmith, ~9,100 followers, 1,600+ posts)",
      "4.3★ across 44 reviews",
      "Located at 810 Ongpin St.",
      "No independent website",
    ],
    problem: "Gold priced by weight is decided fast, in person — a customer weighing options beforehand searches \"Mayfair\" and can land on unrelated results, including a similarly-named account, before the Ongpin address narrows it down.",
    fixes: [
      "The 810 Ongpin St. address stated clearly, up front",
      "Gold-by-weight buying explained so a customer knows what to expect before visiting",
      "44 reviews presented as Mayfair's own reputation",
      "A direct way to ask a question before making the trip",
    ],
    closing: "44 reviews already trust this shop. A website makes sure a search for \"Mayfair\" actually leads to 810 Ongpin.",
  },
  "rb-jewelry-concept": {
    name: "RB Jewelry",
    conceptPath: "/rb-jewelry-concept",
    hero: "/rbjewelry/hero.jpg",
    risk: "You're about to spend a significant amount on a fine jewelry piece.",
    reputation: { rating: "4.8", reviewCount: 23 },
    opening: "RB Jewelry already has something a website cannot manufacture: a 4.8★ rating across 23 reviews at 813 Ongpin St.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "\"RB Jewelry\" is a generic-sounding name likely to overlap with unrelated sellers in a general search" },
    findings: [
      "An active Facebook page",
      "4.8★ across 23 reviews",
      "Located at 813 Ongpin St., Binondo/Sta. Cruz",
      "No Instagram account and no independent website",
      "A generic name (\"RB Jewelry\") that could match unrelated sellers in a search",
    ],
    problem: "\"RB Jewelry\" is generic enough that a search doesn't reliably lead to this specific 813 Ongpin shop over an unrelated business using similar initials.",
    fixes: [
      "The exact 813 Ongpin St. address stated unmistakably",
      "Fine jewelry categories organized clearly",
      "23 reviews presented as this specific shop's reputation",
      "A distinct identity separate from unrelated similarly-named sellers",
    ],
    closing: "23 reviews already trust this specific shop. A website makes sure a search for \"RB Jewelry\" actually finds it.",
  },
  "cebu-gold-pacific-concept": {
    name: "Cebu Gold Pacific — Gold & Glitz",
    conceptPath: "/cebu-gold-pacific-concept",
    hero: "/cebugold/hero.jpg",
    risk: "You're about to pawn or buy jewelry worth real money.",
    reputation: { rating: null, reviewCount: null, stat: "Since 1997", statLabel: "operating in Cebu City for nearly three decades" },
    opening: "Cebu Gold Pacific already has something many concept businesses don't: 28 years operating in Cebu City, since 1997, combining a pawnshop and jewelry counter under one roof — and its Gold & Glitz jewelry line already has a live website.",
    presence: { facebook: "good", instagram: "weak", website: "good", extra: "Gold & Glitz's jewelry line already has its own live page at cebugoldpacific.com/gold-and-glitz-jewelry, plus a modestly-followed Instagram (@goldandglitzph, roughly 250 followers)." },
    findings: [
      "An active Facebook page and a live jewelry website (cebugoldpacific.com/gold-and-glitz-jewelry)",
      "A modest, real Instagram presence (@goldandglitzph)",
      "No independently verified public review count",
      "Operating since 1997 — 28 years on Osmeña Blvd., Cebu City",
      "Combined pawnshop and jewelry services",
    ],
    problem: "28 years in one Cebu City location is a real, verifiable history — but the existing Gold & Glitz jewelry page is a small section of the larger Cebu Gold Pacific corporate site, not a page built to sell jewelry, explain how the pawnshop and jewelry sides work together, or let a customer browse pieces on its own terms.",
    fixes: [
      "The 1997 founding and 28-year Osmeña Blvd. history told clearly",
      "Pawnshop and jewelry services explained as one combined operation",
      "A jewelry-first browsing experience, distinct from the corporate pawnshop site",
      "A direct way to ask about an appraisal or a piece",
    ],
    closing: "28 years in one place is a real, rare kind of trust. A dedicated jewelry site builds on the existing Gold & Glitz page, rather than replacing it.",
  },
  "myrnas-jewelry-concept": {
    name: "Myrna's Jewelry",
    conceptPath: "/myrnas-jewelry-concept",
    hero: "/myrna/hero.jpg",
    risk: "You're about to order a custom name piece, or trust them with a repair.",
    reputation: { rating: null, reviewCount: null, stat: "Years of pickups", statLabel: "shared as customer photos on Facebook" },
    opening: "Myrna's Jewelry already has something a website cannot manufacture: years of custom name-piece and repair work, documented well enough that customers have made two separate YouTube videos about the shop.",
    presence: { facebook: "good", instagram: "weak", website: "none", extra: "Two customer-made YouTube videos exist showing the shop, in addition to Facebook, an Instagram account (@myrnasjewelry, activity level not independently confirmed), and a Waze listing" },
    findings: [
      "An active Facebook page, an Instagram account, a Waze listing, and two customer-made YouTube videos",
      "No independently verified public review count",
      "A Chinatown Gold Center stall specializing in custom name necklaces, rings and wedding bands",
      "No independent website",
    ],
    problem: "A stall inside a larger gold center building is genuinely hard to find on a first visit — Myrna's already has real proof of trust in the form of customer-made videos, but nothing brings that proof together with clear directions to Stall 17-B.",
    fixes: [
      "Exact directions to Stall 17-B, Chinatown Gold Center, made clear before someone visits",
      "Custom name-jewelry, wedding bands and repair services organized clearly",
      "The customer-made videos referenced as real proof of the shop's work",
      "A direct way to describe a custom order before visiting",
    ],
    closing: "Customers already made their own videos about this shop. A website is where that proof finally gets organized with the directions people actually need.",
  },
  "agencia-belen-pawnshop-concept": {
    name: "Agencia Belen Pawnshop & Jewelry",
    conceptPath: "/agencia-belen-pawnshop-concept",
    hero: "/agenciabelen/hero.jpg",
    risk: "You're about to pawn valuables, or buy a wedding ring, worth real money.",
    reputation: { rating: "4.6", reviewCount: 21 },
    opening: "Agencia Belen already has something a website cannot manufacture: a 4.6★ rating across 21 reviews, and a Dumaguete reputation old enough to be known locally as \"Ang Karaang Kasaligan\" — the old trusted one.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "A genuinely active Instagram account (@agencia_belen_dgte) has over 1,100 followers." },
    findings: [
      "An active Facebook page and Instagram (@agencia_belen_dgte, ~1,100+ followers)",
      "4.6★ across 21 reviews",
      "A long-running Dumaguete pawnshop and jewelry store on Perdices St.",
      "Publicly known locally by the tagline \"Ang Karaang Kasaligan sa Dumaguete\" (the old trusted one)",
      "No independent website",
    ],
    problem: "Being known locally as \"the old trusted one\" is a genuinely strong reputation — but that local trust doesn't reach someone new to Dumaguete searching online, where nothing currently explains the Highest Appraisals promise or the Perdices St. location clearly.",
    fixes: [
      "The \"Ang Karaang Kasaligan\" reputation explained to people who aren't already locals",
      "Pawnshop appraisals and jewelry sales shown as distinct services",
      "The Highest Appraisals promise stated clearly",
      "The Perdices St. location and a direct way to ask a question",
    ],
    closing: "Locals already call this \"the old trusted one.\" A website introduces that same trust to everyone else.",
  },
  "pq-jewelries-concept": {
    name: "PQ Jewelries & Accessories",
    conceptPath: "/pq-jewelries-concept",
    hero: "/pqjewelries/storefront.jpg",
    risk: "You're about to commit to a gold piece, or a layaway plan, based only on a Facebook post.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "PQ Jewelries & Accessories already has something a website cannot manufacture: a perfect 5.0★ rating from its public reviews.",
    presence: { facebook: "good", instagram: "weak", website: "none", extra: "The only Instagram content found is a single post link, not a maintained profile" },
    findings: [
      "An active Facebook page",
      "5.0★ from its public reviews",
      "An Instagram presence limited to a single post, not a maintained profile",
      "No independent website",
    ],
    problem: "A layaway plan is a multi-visit commitment — a customer wants to understand the terms clearly before starting one, but right now that information exists only inside individual Facebook posts, not a page built to explain it once.",
    fixes: [
      "Layaway terms explained clearly in one place",
      "Gold and accessory categories organized so they can be browsed",
      "The 5.0★ reputation presented as PQ's own",
      "A direct way to ask about a specific piece or plan",
    ],
    closing: "A perfect rating is real proof. A website is where a layaway plan finally gets explained clearly, once, instead of post by post.",
  },
  "belcina-jewelry-concept": {
    name: "Belciña Jewelry Incorporated",
    conceptPath: "/belcina-jewelry-concept",
    hero: "/belcina/storefront-day.jpg",
    risk: "You're about to spend a meaningful amount at a jewelry store you may never have visited before.",
    reputation: { rating: "4.2", reviewCount: 35 },
    opening: "Belciña Jewelry already has something a website cannot manufacture: years of local trade from a recognizable art-deco corner building in downtown Dumaguete.",
    presence: { facebook: "good", instagram: "weak", website: "none", extra: "A modest but active Instagram account (@belcinajewelryinc) has roughly 250 followers." },
    findings: [
      "An active Facebook page and a modest, active Instagram (@belcinajewelryinc, ~250 followers)",
      "4.2★ across 35 reviews",
      "Housed in a recognizable art-deco corner building — Maria Cristina cor. San Juan St., Dumaguete City",
      "Years of local trade in jewelry, watches and gold",
      "No independent website",
    ],
    problem: "A recognizable landmark building is a real, distinctive local asset — but for someone who hasn't visited Dumaguete before, nothing online currently uses that building, or the years of trade behind it, to build confidence before they walk in.",
    fixes: [
      "The art-deco corner building shown clearly, so it's recognizable before someone arrives",
      "Jewelry, watch and gold categories organized clearly",
      "The years of local trade told as Belciña's own history",
      "A direct way to ask about a specific piece",
    ],
    closing: "A recognizable building and years of local trade are real assets. A website is where a first-time visitor gets to see both before they walk in.",
  },
  "jehannah-goldsmith-concept": {
    name: "Jehannah Goldsmith Jewelry Repair Shop",
    conceptPath: "/jehannah-goldsmith-concept",
    hero: "/jehannah/prod-panther-bangle.jpg",
    risk: "You're about to hand over gold, or place a custom order, based only on Facebook posts.",
    reputation: { rating: null, reviewCount: null, stat: "An active following", statLabel: "built through custom work and honest repairs" },
    opening: "Jehannah Goldsmith already has something a website cannot manufacture: an active following built specifically around custom gold and silver work and honest repairs.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "The current storefront location isn't confirmed publicly — customers are asked to message first to confirm it" },
    findings: [
      "An active Facebook page",
      "No independently verified public review count",
      "Custom gold and silver jewelry, repair, and scrap-gold and pawnshop-receipt buying, all offered",
      "The current Cebu-area storefront location isn't confirmed publicly — customers are asked to message first",
      "No Instagram account and no independent website",
    ],
    problem: "Asking every customer to message first just to confirm the storefront location is a real, avoidable friction point — a page with a clear, current address would remove a step that currently happens on every single inquiry.",
    fixes: [
      "A clear, current storefront address, removing the need to ask every time",
      "Custom gold/silver work, repair and scrap-gold buying shown as distinct services",
      "The honest-repair reputation presented clearly",
      "A direct way to describe a custom order or repair before visiting",
    ],
    closing: "An active following already trusts this shop's work. A website removes the one extra step every new customer currently has to take just to find it.",
  },
  "ac-tajanlangit-construction-concept": {
    name: "A.C. Tajanlangit Construction",
    conceptPath: "/ac-tajanlangit-construction-concept",
    hero: "/construction/tajanlangit/hero-resort-aerial.jpg",
    risk: "You're about to trust a contractor with a resort, commercial or residential project that could run into the millions of pesos.",
    reputation: { rating: "5.0", reviewCount: 10 },
    opening: "A.C. Tajanlangit Construction already has something most businesses in this batch don't: a perfect 5.0★ rating across 10 reviews, a real company website at actajanlangit.com, and real resort and commercial work delivered in-house from design through build.",
    presence: { facebook: "good", instagram: "good", website: "good", extra: "actajanlangit.com is a real, active company website with its own About and History pages. An Instagram account (@actajanlangit) is also active, alongside the Facebook page." },
    findings: [
      "An active Facebook page",
      "A real company website at actajanlangit.com, including About and History pages",
      "An active Instagram account",
      "5.0★ across 10 reviews",
      "In-house design-through-build for resort, commercial and residential projects",
      "Based in Iloilo City",
    ],
    problem: "A.C. Tajanlangit already has a real website and a strong reputation — the opportunity is presenting the in-house design-through-build process and real project history (Two Seasons Boracay, Bernwood Tower and others) in a way that matches the scale of the projects themselves, not replacing something that doesn't exist.",
    fixes: [
      "Resort, commercial and residential project types shown as distinct capabilities",
      "The in-house design-through-build process explained clearly",
      "Real project photography organized as a portfolio",
      "A modernized presentation of the existing site, not a replacement of it",
    ],
    closing: "A perfect 5.0★ record and a real website are already in place. This concept shows what a refreshed version of that same presence could look like.",
  },
  "megward-construction-concept": {
    name: "Megward Construction",
    conceptPath: "/megward-construction-concept",
    hero: "/construction/megward/package-elegant.jpg",
    risk: "You're about to trust a builder with the home your family will live in — whether it's a ₱500,000 renovation or a ₱3,000,000+ new build.",
    reputation: { rating: "5.0", reviewCount: 16 },
    opening: "Megward Construction already has something rare in this batch: a 5.0★ rating across 16 reviews, a real company website at megwardconstruction.com, and published house-package pricing for residential design-build work in Bacolod City.",
    presence: { facebook: "good", instagram: "none", website: "good", extra: "megwardconstruction.com is a real, active company site — the same domain and the Bare/Standard/Elegant Finish package pricing appear on Megward's own promotional flyers." },
    findings: [
      "An active Facebook page",
      "A real company website at megwardconstruction.com",
      "5.0★ across 16 reviews",
      "Published house-package pricing (Bare, Standard, Elegant Finish)",
      "Residential design-build — consultation through permits and turnover",
      "Based in Bacolod City",
    ],
    problem: "Megward already has a website and a real reputation — the opportunity is presenting the consultation-through-turnover process, the house packages and 16 five-star reviews in one place that matches the quality of the work, rather than leaving a family to piece it together from Facebook and a flyer.",
    fixes: [
      "The consultation-through-turnover process explained step by step",
      "Residential design-build packages shown clearly, matching the company's real pricing",
      "Real completed homes organized as a portfolio",
      "A modernized presentation of the existing site and packages, not a replacement of them",
    ],
    closing: "16 families already rated this builder 5.0★, and Megward already has a real site. This concept shows what a refreshed version of that presence could look like.",
  },
  "g-uymatiao-jr-construction-concept": {
    name: "G. Uymatiao Jr. Construction",
    conceptPath: "/g-uymatiao-jr-construction-concept",
    hero: "/construction/uymatiao/products-catalog.jpg",
    risk: "You're about to trust a contractor with an institutional or commercial project — real money, decided based on whatever you can find about them online.",
    reputation: { rating: "5.0", reviewCount: 6 },
    opening: "G. Uymatiao Jr. Construction already has something a website cannot manufacture: 43 years building Dumaguete's institutional and commercial landscape, since 1982.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "5.0★ across 6 reviews",
      "Operating since 1982 under Engr. Gregorio Lim Uymatiao Jr.",
      "Architectural, engineering, project management and construction-materials supply all under one company",
      "No Instagram account and no independent website",
    ],
    problem: "43 years of institutional and commercial construction history in Dumaguete is a rare, verifiable track record — but a developer evaluating contractors today has no single page showing that history alongside the actual services the company now offers.",
    fixes: [
      "Engr. Gregorio Lim Uymatiao Jr. and the 1982 founding told as the company's own story",
      "Architectural, engineering, project management and materials supply shown as one combined capability",
      "Institutional and commercial project history organized as a portfolio",
      "A direct way to start a project conversation",
    ],
    closing: "43 years in Dumaguete is a real, rare credential. A website is where that history finally gets to speak for itself.",
  },
  "alexis-construction-concept": {
    name: "Alexis Construction",
    conceptPath: "/alexis-construction-concept",
    hero: "/construction/alexis/site-groundbreaking.jpg",
    risk: "You're about to trust a builder with your family home — whether it's a ₱1,000,000 renovation or a ₱3,000,000+ new build.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "Alexis Construction already has something a website cannot manufacture: active current work across five named Iloilo sites — Fatima Jaro, Lapaz, Hacienda Verde, Green Meadows and Palmyra.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "5.0★ across 5 reviews",
      "Public updates showing current, active work across five named Iloilo project sites",
      "Site teams based in Iloilo",
      "No Instagram account and no independent website",
    ],
    problem: "Five active project sites at once is real, current proof of capacity — but a family comparing builders has no single page showing that this contractor is genuinely active right now, only individual Facebook updates spread across months.",
    fixes: [
      "Current active projects — Fatima Jaro, Lapaz, Hacienda Verde, Green Meadows, Palmyra — shown together as a live portfolio",
      "Home-building services tailored to style and budget explained clearly",
      "The Iloilo site-team structure explained",
      "A direct way to start a project conversation",
    ],
    closing: "Five active sites at once already prove real capacity. A website is where a family sees that proof in one place instead of scattered posts.",
  },
  "jra-home-builders-concept": {
    name: "JRA HOME Builders",
    conceptPath: "/jra-home-builders-concept",
    hero: "/construction/jra/render-1.png",
    risk: "You're about to trust a builder with the home your family will live in for years, based on whatever you can piece together across a website and several social profiles.",
    reputation: { rating: "5.0", reviewCount: 6 },
    opening: "JRA HOME Builders already has something most businesses in this batch don't: a 5.0★ rating across 6 reviews, active Facebook pages for both its Bacolod and Iloilo offices, an active Instagram account, and its own website at jrahomebuilders.com with a finished-projects portfolio.",
    presence: { facebook: "good", instagram: "good", website: "weak", extra: "JRA has active Facebook pages for both its Bacolod (facebook.com/jrahomebuilders) and Iloilo (facebook.com/jrailoilo) offices, plus a company website at jrahomebuilders.com that includes an About page and a Finished Projects portfolio. The website did not load reliably in an automated check, so it's worth confirming it's current." },
    findings: [
      "Active Facebook pages for both the Bacolod and Iloilo offices",
      "An active Instagram account",
      "5.0★ across 6 reviews",
      "A company website at jrahomebuilders.com, including a finished-projects portfolio",
      "Residential design-and-build serving Bacolod City, Negros and beyond",
    ],
    problem: "JRA already has a website, a finished-projects portfolio, and active Facebook and Instagram pages — the opportunity isn't discoverability, it's presenting that real completed work in one modernized, consistent place instead of split across four different profiles and a slow-loading site.",
    fixes: [
      "Real completed-home photography organized as an actual portfolio, alongside the design renders",
      "Residential design-and-build services explained clearly",
      "The Bacolod and Iloilo offices and the Negros-wide service area shown together",
      "A more reliable, modernized presentation than the existing site",
    ],
    closing: "JRA already built a real website, a real portfolio, and real audiences on Facebook and Instagram. This concept shows what a refreshed, more dependable version of that presence could look like.",
  },
  "k-elman-construction-concept": {
    name: "K. Elman Construction",
    conceptPath: "/k-elman-construction-concept",
    hero: "/construction/kelman/hero-banner.jpg",
    risk: "You're about to trust a contractor with a home, apartment build, or renovation — real money, decided on Facebook alone.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "K. Elman Construction already has something a website cannot manufacture: a 5.0★ rating across 5 reviews for residential and apartment work across three towns — Valencia, Dumaguete and Tanjay.",
    presence: { facebook: "good", instagram: "weak", website: "none", extra: "The only Instagram content found is a single reel, not a full business profile" },
    findings: [
      "An active Facebook page",
      "5.0★ across 5 reviews",
      "Residential construction, apartments and renovation work across Valencia, Dumaguete and Tanjay",
      "Instagram presence limited to a single reel, not a maintained business profile",
      "No independent website",
    ],
    problem: "Serving three separate towns is a real strength, but there's no single page explaining that reach — someone in Tanjay checking Facebook has no easy way to confirm K. Elman actually builds there too.",
    fixes: [
      "Valencia, Dumaguete and Tanjay all shown as active service areas",
      "Residential, apartment and renovation work shown as distinct capabilities",
      "Real project photography organized as a portfolio",
      "A direct way to start a project conversation from any of the three towns",
    ],
    closing: "Three towns already trust this contractor with real work. A website makes that reach obvious instead of something a client has to discover by asking around.",
  },
  "rkd-plus-construction-concept": {
    name: "RKD PLUS Construction & Interiors",
    conceptPath: "/rkd-plus-construction-concept",
    hero: "/construction/rkdplus/site-dumaguete.jpg",
    risk: "You're about to trust a licensed contractor with a project that could run from ₱500,000 to ₱3,000,000 or more.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "RKD PLUS Construction & Interiors already has something a website cannot manufacture: a real, verifiable PCAB License No. 61020 and a 5.0★ rating across 5 reviews.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "5.0★ across 5 reviews",
      "PCAB License No. 61020 — a specific, checkable government license number",
      "Construction and interior services delivered together",
      "Based in Sibulan, Negros Oriental",
      "No Instagram account and no independent website",
    ],
    problem: "A specific, checkable PCAB license number is exactly the kind of proof a cautious client looks for before signing a contract — but right now it only shows up inside a Facebook post, not on a page built to make it easy to verify.",
    fixes: [
      "PCAB License No. 61020 stated clearly and prominently",
      "Construction and interior design services shown as one combined offering",
      "The Sibulan, Negros Oriental base and service area",
      "A direct way to start a licensed-contractor conversation",
    ],
    closing: "A real PCAB license is exactly the proof a cautious client looks for. A website is where that license actually gets seen.",
  },
  "algon-engineering-construction-concept": {
    name: "Algon Engineering Construction Corporation",
    conceptPath: "/algon-engineering-construction-concept",
    hero: "/construction/algon/bridge-infrastructure.jpg",
    risk: "You're about to trust a contractor with an infrastructure or industrial project — real money, decided on a Facebook page.",
    reputation: { rating: "4.9", reviewCount: 8 },
    opening: "Algon Engineering Construction already has something most businesses in this batch don't: 59 years of infrastructure, industrial and institutional work in Davao City since 1966, and a real company website at algonengineering.com.",
    presence: { facebook: "good", instagram: "none", website: "good", extra: "algonengineering.com is a real, active company website with Home and Contact Us pages." },
    findings: [
      "An active Facebook page",
      "A real company website at algonengineering.com",
      "4.9★ across 8 reviews",
      "Operating since 1966 — 59 years of general engineering construction",
      "Infrastructure, industrial and institutional project experience",
      "Based in Davao City",
    ],
    problem: "59 years is a genuinely rare track record for an infrastructure contractor — Algon already has a website, but the opportunity is presenting that history alongside the institutional and industrial work Algon actually does, at a scale that matches what a nearly 60-year-old firm's bidders expect to see.",
    fixes: [
      "The 1966 founding and 59-year history told as the company's own story",
      "Infrastructure, industrial and institutional capabilities shown clearly",
      "Real project photography organized as a portfolio",
      "A modernized presentation of the existing site, not a replacement of it",
    ],
    closing: "59 years in Davao is a rare, verifiable credential, and Algon already has a website. This concept shows what a refreshed version of that presence could look like.",
  },
  "khan-kon-chi-construction-concept": {
    name: "Khan Kon Chi Construction",
    conceptPath: "/khan-kon-chi-construction-concept",
    hero: "/construction/khankonchi/logo.jpg",
    risk: "You're about to trust a corporation with a multi-million-peso infrastructure contract — decided on whatever comes up in a search.",
    reputation: { rating: "4.7", reviewCount: 24 },
    opening: "Khan Kon Chi Construction already has something a website cannot manufacture: documented major port and dredging contracts on public procurement and news records across the Philippines.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "4.7★ across 24 reviews",
      "Major port and dredging contracts documented in public procurement and news records",
      "Based in Davao City, working nationally",
      "No Instagram account and no independent website",
    ],
    problem: "Public procurement records already document real, large-scale port and dredging work — but that history is scattered across government and news records, not presented anywhere as Khan Kon Chi's own credentials.",
    fixes: [
      "Documented port and dredging contract history shown as the company's own track record",
      "24 reviews and a 4.7★ rating presented clearly",
      "Infrastructure capability explained for agencies and companies evaluating bidders",
      "A direct way to start a project or bid conversation",
    ],
    closing: "Public records already prove this company handles major infrastructure work. A website is where that proof becomes the company's own first impression.",
  },
  "egi-construction-concept": {
    name: "E.G. & I Construction Corporation",
    conceptPath: "/egi-construction-concept",
    hero: "/construction/egi/silo-project.jpg",
    risk: "You're about to trust a contractor with an industrial project worth real money — decided on a Facebook page alone.",
    reputation: { rating: "4.8", reviewCount: 12 },
    opening: "E.G. & I Construction already has something most businesses in this batch don't: 32 years of industrial construction in Cebu since 1993, and a company website at egi.cradle.work.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "egi.cradle.work is a real company site with an About page describing E.G. & I's licensing and services, though it did not load reliably in an automated check — worth confirming it's current." },
    findings: [
      "An active Facebook page",
      "A company website at egi.cradle.work",
      "4.8★ across 12 reviews",
      "Operating since 1993 — 32 years in Cebu",
      "Industrial project experience, including silo and structural work",
    ],
    problem: "32 years of industrial construction is a real, specific credential for the kind of company that needs a contractor comfortable with large structural work — E.G. & I already has a website, but the opportunity is presenting that history and real project photography somewhere more reliable and easier to trust than the current site.",
    fixes: [
      "The 1993 founding and 32-year Cebu history told clearly",
      "Industrial and structural project capability, including real silo-project photography",
      "A portfolio organized around completed industrial work",
      "A more reliable, modernized presentation than the existing site",
    ],
    closing: "32 years of industrial work in Cebu is a real credential, and a company website already exists. This concept shows what a more dependable version of that presence could look like.",
  },
  "ac-rivero-development-concept": {
    name: "A.C. Rivero Development Corporation",
    conceptPath: "/ac-rivero-development-concept",
    hero: "/construction/acrivero/logo.png",
    risk: "You're about to trust a developer with a government or housing project — with almost nothing online to check them against.",
    reputation: { rating: null, reviewCount: null, stat: "A real development track record", statLabel: "in government and housing projects" },
    opening: "A.C. Rivero Development already has something a website cannot manufacture: a real, active engineering and development company at 2929 Burgos Extension, Bacolod City.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "No independent company website (own domain) was found — only Facebook, directory listings and contact-aggregator pages" },
    findings: [
      "An active Facebook page under the company's own name",
      "A confirmed office address — 2929 Burgos Extension, Bacolod City",
      "Listed as an active engineering/contracting company in business directories",
      "No independently verified public review count",
      "No independent company website of its own",
    ],
    problem: "Government and housing developers are exactly the kind of client who checks a company's track record carefully before engaging — but right now, that verification means piecing the company together from directory listings and contact-aggregator pages rather than one page the company controls.",
    fixes: [
      "Government and housing project experience shown as A.C. Rivero's own, clearly attributed track record",
      "The confirmed Bacolod City address and contact details in one place",
      "Development services explained for agencies and housing clients specifically",
      "A direct, current way to start a project conversation",
    ],
    closing: "A real, active company deserves one page it controls — not a scattering of directory listings.",
  },
  "constantin-design-build-concept": {
    name: "Constantin Design & Project Management",
    conceptPath: "/constantin-design-build-concept",
    hero: "/construction/constantin/poolvilla-render.jpg",
    risk: "You're about to trust a design studio with a home or space you'll live in for years, based on whatever their current site happens to show.",
    reputation: { rating: null, reviewCount: null, stat: "An established Davao design-build studio", statLabel: "operating since 2012, with a real company website" },
    opening: "Constantin Design & Project Management already has something most businesses in this batch don't: a real, working company website at constantintegratedph.com, and an established operating history in Davao City since 2012.",
    presence: { facebook: "good", instagram: "none", website: "good", extra: "constantintegratedph.com is a real, active company site with a project portfolio, about page and contact details, independently referenced by a Sunstar Davao business feature. The studio's Facebook page is facebook.com/constantin.PH." },
    findings: [
      "An active Facebook page",
      "A real, working company website at constantintegratedph.com",
      "Operating since 2012, delivering design, construction and renovation work in Davao City",
      "A growing portfolio of real design-build projects, including pool villas",
      "No independently verified public review count",
    ],
    problem: "Constantin already has a real website and a working portfolio — this isn't a discoverability problem. The opportunity is presenting that established, since-2012 track record in a way that matches the quality of the design work itself.",
    fixes: [
      "Real project renders and completed work organized as an actual portfolio",
      "Design and project-management services explained as distinct offerings",
      "The studio's since-2012 history and design approach explained clearly",
      "A modernized presentation of the existing site, not a replacement of it",
    ],
    closing: "Constantin already built a real studio and a real website. This concept shows what a refreshed version of that same site could look like.",
  },
  "tuanson-construction-concept": {
    name: "Tuanson Construction",
    conceptPath: "/tuanson-construction-concept",
    hero: "/construction/tuanson/onsite-install.jpg",
    risk: "You're about to trust a licensed contractor with a project that could run from ₱500,000 to ₱3,000,000 or more.",
    reputation: { rating: null, reviewCount: null, stat: "Engineer-owned contractor", statLabel: "operating in Talisay City since 1995" },
    opening: "Tuanson Construction already has something a website cannot manufacture: nearly three decades operating in Talisay City, Cebu, under civil engineer Ricardo Tuanson, with a real Facebook presence documenting its site work.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "Any PCAB classification claim could not be independently confirmed and is not stated here — a specific license category should only be published once the company confirms its current standing." },
    findings: [
      "An active Facebook page documenting site and community work",
      "Operating in Talisay City, Cebu since 1995, under civil engineer Ricardo Tuanson",
      "No independently verified public review count",
      "No Instagram account and no independent website",
      "No PCAB classification is claimed here — it should be added only once confirmed directly with the company",
    ],
    problem: "Nearly 30 years in business and real engineering leadership are genuine credentials, but nothing online currently brings that history, the company's licensing, and its actual project scale together in one place a prospective client can check.",
    fixes: [
      "Ricardo Tuanson's engineering background and the company's 1995 founding stated clearly",
      "Talisay City, Cebu base and service capacity explained",
      "Real project and community-outreach photography organized as a portfolio",
      "A direct way to start a project conversation",
    ],
    closing: "Nearly three decades in business is already a real credential. A website is where that history — and, once confirmed, the company's current licensing — actually gets explained to a client who doesn't know it yet.",
  },
  "cubeworx-builders-concept": {
    name: "Cubeworx Builders & Development",
    conceptPath: "/cubeworx-builders-concept",
    hero: "/construction/cubeworx/completed-building.jpg",
    risk: "You're about to trust a builder with your home or commercial space — real money, decided on a Facebook page.",
    reputation: { rating: null, reviewCount: null, stat: "An active local builder", statLabel: "with real, completed jobsite work" },
    opening: "Cubeworx Builders & Development already has something a website cannot manufacture: real, completed residential and commercial jobsite work in Dumaguete City.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "No independently verified public review count",
      "Real, completed residential and commercial building work",
      "Based in Dumaguete City",
      "No Instagram account and no independent website",
    ],
    problem: "Completed jobsite work is the most convincing proof a builder can show, but right now that proof only exists as individual Facebook posts, with no single page organizing it into an actual portfolio a prospective client can review before calling.",
    fixes: [
      "Completed residential and commercial projects organized as a real portfolio",
      "Building services explained clearly for both residential and commercial clients",
      "The Dumaguete City base stated plainly",
      "A direct way to start a project conversation",
    ],
    closing: "Real completed buildings are Cubeworx's strongest proof. A website is where that proof finally gets organized instead of scattered across old posts.",
  },
  "dpx-negros-construction-concept": {
    name: "Dumaguete PhilX Condev (DPX)",
    conceptPath: "/dpx-negros-construction-concept",
    hero: "/construction/dpx/stonework-project.jpg",
    risk: "You're about to trust a design-build firm with your project, based on whatever their current website happens to show.",
    reputation: { rating: null, reviewCount: null, stat: "A full-service design-build firm", statLabel: "serving Negros Oriental and Siquijor since 2014" },
    opening: "DPX already has something most businesses in this batch don't: a real, working company website at negrosconstruction.com, and a named founder — Gordon Mckissock — behind an operation running since 2014.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "negrosconstruction.com is a real, functioning company site — \"Dumaguete PhilX Condev / DPX\" and \"Negros Construction\" are the same company operating under two consistent names, not two competing identities" },
    findings: [
      "An active Facebook page",
      "A real, functioning company website at negrosconstruction.com",
      "Operating since 2014, led by founder Gordon Mckissock",
      "\"DPX\" and \"Negros Construction\" are the same company's two consistent brand names, used together on its own site",
      "No independently verified public review count",
    ],
    problem: "DPX already has a real website and a named founder — this isn't a discoverability problem. The opportunity is that the current site is dated enough that it doesn't yet present the company the way an 11-year, multi-service design-build operation deserves to be presented.",
    fixes: [
      "Gordon Mckissock introduced by name as founder",
      "Dumaguete City and Siquijor coverage stated clearly",
      "Design, build, solar, and other real services organized clearly",
      "A modernized presentation of the DPX / Negros Construction identity, not a replacement of it",
    ],
    closing: "DPX already built a real company and a real website. This concept shows what a refreshed version of that same site could look like.",
  },
  "berguia-law-davao-concept": {
    name: "Berguia Law",
    conceptPath: "/berguia-law-davao-concept",
    hero: "/law/berguia-law-davao-concept/reception-standing.jpg",
    risk: "You're searching for a Davao lawyer during a stressful moment — a family matter or criminal case. Before calling, you'd want to know that Atty. Berguia is a former registered nurse who became a lawyer, what he actually handles, and whether people locally already trust him with cases like yours.",
    reputation: { rating: "5.0", reviewCount: 76 },
    opening: "Berguia Law already has something a website cannot manufacture: a 5.0★ reputation built from 76 public reviews, and a distinctive founder story — a registered nurse who became a Davao lawyer.",
    presence: { facebook: "none", instagram: "none", website: "none", extra: "A LinkedIn profile for Atty. Berguia" },
    findings: [
      "Public reviews on Google Maps — 5.0★ from 76 people",
      "A personal LinkedIn profile for Atty. Berguia",
      "No verified Facebook page",
      "No Instagram account",
      "No independent law-firm website",
    ],
    problem: "People searching for a Davao lawyer during a stressful moment can't find anything Berguia Law controls directly online — no Facebook, no website, just Maps reviews and a personal LinkedIn profile. The reputation is real; the digital footprint doesn't match it yet.",
    fixes: [
      "His actual practice areas — civil, criminal and family law",
      "The nurse-to-lawyer story that makes him memorable",
      "Real photos of the Davao office",
      "A direct way to request a consultation",
    ],
    closing: "The goal isn't to replace how people already find Atty. Berguia. It's to give that reputation, and that story, one place of its own to live.",
  },
  "scale-documentation-makati-concept": {
    name: "SCALE Documentation Services",
    conceptPath: "/scale-documentation-makati-concept",
    hero: null,
    risk: "You need a document notarized in Makati — something that has to hold up legally. Before visiting Cityland Herrera Tower, you'd want to know exactly what to bring and whether the 500+ people who already left reviews actually got good service, since almost none of that is explained anywhere online.",
    reputation: { rating: "5.0", reviewCount: 543 },
    opening: "SCALE Documentation Services already has something a website cannot manufacture: hundreds of public reviews and a real Makati CBD office.",
    presence: { facebook: "none", instagram: "none", website: "none" },
    findings: [
      "543 public reviews on Google Maps and directory listings",
      "No verified Facebook page",
      "No Instagram account",
      "No standalone website",
      "No listed email address",
    ],
    problem: "SCALE has one of the strongest review footprints in this entire research batch, yet someone searching for the business today lands only on Maps and third-party directories — nothing SCALE itself controls explains what to bring, what the process looks like, or how to reach the office directly.",
    fixes: [
      "The notarial process explained clearly",
      "The exact office location inside Cityland Herrera Tower",
      "543 reviews presented as SCALE's own reputation",
      "A direct way to reach the office",
    ],
    closing: "543 people have already told the world SCALE does good work. Right now, that reputation lives entirely on platforms SCALE doesn't control.",
  },
  "meking-law-firm-concept": {
    name: "Meking Law Firm",
    conceptPath: "/meking-law-firm-concept",
    hero: "/law/meking-law-firm-concept/consultation.jpg",
    risk: "You're dealing with something serious — a custody dispute, a land title problem, a criminal complaint — and searching for a lawyer in Quezon City or Angeles City. Before calling either branch, you'd want to know which lawyer you'd actually be working with and what they've handled before.",
    reputation: { rating: "5.0", reviewCount: 52 },
    opening: "Meking Law Firm already has something a website cannot manufacture: a 5.0★ reputation across 52 reviews, and a founder with a real Supreme Court case to her name.",
    presence: { facebook: "weak", instagram: "none", website: "none" },
    findings: [
      "A Facebook page for the Angeles City branch",
      "52 public reviews",
      "No Instagram account",
      "No independent firm website",
      "No single page explaining both the QC office and the Pampanga branch",
    ],
    problem: "Someone searching for Meking Law Firm finds a Facebook page for one branch, but nothing that clearly lays out both offices, which lawyer they'd actually be speaking with, or what the firm handles — despite a 5.0★ reputation and a real Supreme Court case behind the name.",
    fixes: [
      "Both the Quezon City office and Angeles City branch on one page",
      "Atty. Meking's real background and case history",
      "Practice areas laid out clearly",
      "A direct consultation request",
    ],
    closing: "The goal isn't to replace Facebook. It's to give it, and every branch, one professional destination to point to.",
  },
  "gutierrez-legal-team-concept": {
    name: "The Gutierrez Legal Team",
    conceptPath: "/gutierrez-legal-team-concept",
    hero: null,
    risk: "You need an estate plan or property transfer handled — the kind of thing that touches nearly everything your family owns. Before reaching out, you'd want to know this multidisciplinary team of lawyers, accountants and real-estate brokers is really at one address, not scattered between conflicting Taguig and Parañaque listings.",
    reputation: { rating: "5.0", reviewCount: 10 },
    opening: "The Gutierrez Legal Team already has something a website cannot manufacture: a genuinely rare combination — lawyers, accountants and real-estate brokers working as one family team.",
    presence: { facebook: "none", instagram: "none", website: "weak", extra: "A company LinkedIn page and an older Google Business Site" },
    findings: [
      "A company LinkedIn page",
      "An older Google Business Site",
      "10+ public reviews",
      "No verified Facebook page",
      "Conflicting Taguig vs. Parañaque addresses across listings",
    ],
    problem: "The team's real differentiator — lawyers, accountants and real-estate brokers working together — doesn't come through anywhere online. What does come through is two different addresses in two different cities, leaving a prospective client unsure which one is current.",
    fixes: [
      "One confirmed office address",
      "The integrated legal + accounting + real-estate story explained clearly",
      "Real practice areas and team",
      "A direct way to reach the team",
    ],
    closing: "A team this integrated deserves one clear address online — not two conflicting ones.",
  },
  "tlc-law-firm-mandaue-concept": {
    name: "TLC Law Firm",
    conceptPath: "/tlc-law-firm-mandaue-concept",
    hero: null,
    risk: "You're looking for a Mandaue lawyer for a family matter or contract dispute. Before visiting, you'd want one address you can actually trust — not two different addresses on two different online listings for the same firm.",
    reputation: { rating: null, reviewCount: null, stat: "A working Mandaue office", statLabel: "with a public professional presence" },
    opening: "TLC Law Firm's biggest challenge online right now isn't reputation — it's being identifiable. Two different addresses show up in two different places, and nothing settles which one is current.",
    presence: { facebook: "none", instagram: "none", website: "none", extra: "A LinkedIn profile for Jamaal James Calipayan" },
    findings: [
      "A LinkedIn profile for Jamaal James Calipayan",
      "Two different addresses listed across different online sources",
      "No verified Facebook page",
      "No Instagram account",
      "No independent firm website",
    ],
    problem: "Searching \"TLC Law Firm\" surfaces conflicting addresses and no single page confirming which one is current — before a prospective client even gets to whether the firm handles their case, they have to work out which listing is actually right.",
    fixes: [
      "One confirmed Mandaue address",
      "Clear practice areas",
      "A single, authoritative identity for the firm",
      "A direct way to reach the office",
    ],
    closing: "A dedicated website creates one canonical answer: this is the firm, this is the address, this is how to reach us.",
  },
  "espiritu-associates-law-bacolod-concept": {
    name: "Espiritu and Associates Law Office",
    conceptPath: "/espiritu-associates-law-bacolod-concept",
    hero: null,
    risk: "You're trusting a Bacolod law office with something serious enough to search for a lawyer over. Before calling, you'd want to know who's actually there today — the office carries a strong 5-star reputation, but almost nothing public explains who the current lawyers are or what they handle.",
    reputation: { rating: "5.0", reviewCount: 7 },
    opening: "Espiritu and Associates already has something a website cannot manufacture: a genuine 5-star local reputation.",
    presence: { facebook: "none", instagram: "none", website: "none" },
    findings: [
      "5.0★ across 7 public reviews",
      "No verified Facebook page",
      "No Instagram account",
      "No independent website",
      "No public explanation of who's currently practicing there",
    ],
    problem: "Espiritu and Associates carries a strong 5-star reputation, but almost nothing public explains who the current lawyers are or what the office actually handles — someone considering the firm has no way to confirm who they'd be working with before they call.",
    fixes: [
      "The current confirmed team, clearly presented",
      "Real practice information",
      "Office location and contact details",
      "A direct way to reach the office",
    ],
    closing: "The missing piece isn't reputation. It's a place for that reputation to explain itself.",
  },
  "trenas-law-iloilo-concept": {
    name: "Treñas Rubias Dela Cruz Lee-Trimañez & Associates",
    conceptPath: "/trenas-law-iloilo-concept",
    hero: "/law/trenas-law-iloilo-concept/office-heritage-wall.jpg",
    risk: "You need a lawyer for a property purchase or lease dispute in Iloilo City. Before calling, you'd want to know who's currently practicing at a firm tracing its roots to 1918, and how that century-old name connects to its current DivinaLaw affiliation.",
    reputation: { rating: null, reviewCount: 35, stat: "Since 1918", statLabel: "an Iloilo legal lineage now affiliated with DivinaLaw" },
    opening: "Treñas already has something a website cannot manufacture: a legal lineage in Iloilo tracing back to 1918, still active today.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "The firm's old trenaslaw.com site no longer has a real presence" },
    findings: [
      "An active Facebook page (facebook.com/trenaslawoffice)",
      "35 public reviews",
      "No Instagram account",
      "The firm's old trenaslaw.com website no longer has a real presence",
    ],
    problem: "Treñas has a real Facebook following and a genuinely rare asset — a legal lineage tracing to 1918 and a current DivinaLaw affiliation — but the firm's own website disappeared, leaving Facebook to carry a history that deserves a proper home.",
    fixes: [
      "The firm's full history, from 1918 to today",
      "Current partners and associates clearly listed",
      "The DivinaLaw affiliation explained",
      "Practice areas across a real 5-page site",
    ],
    closing: "Facebook is already working for Treñas. What's missing is a permanent home for a history this long.",
  },
  "abo-penaranda-law-bgc-concept": {
    name: "Abo & Penaranda Law Firm",
    conceptPath: "/abo-penaranda-law-bgc-concept",
    hero: "/law/abo-penaranda-law-bgc-concept/atty-abo.png",
    risk: "A prospective BGC client checking your firm before a call notices leftover template copy and a lawyer profile that isn't even one of your own — right when they're deciding whether a firm with real Mackrell International standing and a 2018 track record can be trusted with their business.",
    reputation: { rating: "4.8", reviewCount: 18 },
    opening: "Abo & Penaranda already has real advantages most firms in this batch don't: an independent website, active social channels, and genuine Mackrell International standing.",
    presence: { facebook: "good", instagram: "good", website: "bad", extra: "abolawfirm.ph exists, but parts still show template leftovers, including a lawyer profile that isn't from the firm" },
    findings: [
      "An independent website at abolawfirm.ph",
      "Active Facebook and Instagram",
      "A company LinkedIn page",
      "Leftover template copy on parts of the current site, including a lawyer profile that isn't from the firm",
      "4.8★ across 18 public reviews",
    ],
    problem: "Abo & Penaranda already has its own website — a real advantage most firms in this batch don't have. But parts of the current site still show template leftovers, including a profile for a lawyer who isn't actually part of the firm. For a BGC business-law firm, a detail like that lands right when a prospective client is deciding whether to trust the firm with something serious.",
    fixes: [
      "Only the firm's actual, currently confirmed lawyers",
      "Real Mackrell International standing explained clearly",
      "A coherent, professional identity site-wide",
      "Practice areas that match what the firm actually does",
    ],
    closing: "For a firm this established, the website should reinforce that trust — not leave a placeholder lawyer profile as someone's first impression.",
  },
  "kvp-cabahug-law-cebu-concept": {
    name: "KVP Cabahug; Amigo-Escobar & Partners Law",
    conceptPath: "/kvp-cabahug-law-cebu-concept",
    hero: "/law/kvp-cabahug-law-cebu-concept/atty-cabahug.jpg",
    risk: "You're about to hand a lawyer real money and real property — a title transfer, a customs matter, a business registration. Before reaching out, you'd want to know whether Atty. Karina Cabahug, who's also a licensed real-estate broker and appraiser, and her team have actually handled something like your situation before.",
    reputation: { rating: "5.0", reviewCount: 9 },
    opening: "KVP already has something most firms don't: a small set of genuinely detailed reviews that describe exactly what clients value — clear explanations, fast property due diligence, and real support for overseas clients.",
    presence: { facebook: "good", instagram: "none", website: "good", extra: "kvpcabahuglaw.com is live" },
    findings: [
      "A working website at kvpcabahuglaw.com",
      "An active Facebook page",
      "A LinkedIn presence",
      "9 detailed public reviews describing overseas support, property due diligence and contractor disputes",
    ],
    problem: "KVP's value isn't just that it has five-star reviews — it's what those reviews actually describe: clients handling everything remotely from abroad, property purchases verified quickly, disputes explained clearly. Right now, that story is buried inside review platforms instead of being told on the firm's own site.",
    fixes: [
      "Reviews placed next to the specific service they describe",
      "Overseas-client support shown as a real, evidenced strength",
      "Property, business and customs practice areas laid out clearly",
      "The full team, not just the two managing partners",
    ],
    closing: "The value isn't the star rating. It's the story those nine reviews already tell — this concept just makes that story visible.",
  },
  "cpa-tax-bookkeeping-mandaluyong-concept": {
    name: "CPA Tax & Bookkeeping Firm Co.",
    conceptPath: "/cpa-tax-bookkeeping-mandaluyong-concept",
    hero: null,
    risk: "You're about to hand over your business's tax filings and financial records to a Mandaluyong firm that's been operating since 2007. Before sharing anything sensitive, you'd want a clear, current way to confirm exactly who you're working with and how to reach them.",
    reputation: { rating: null, reviewCount: null, stat: "Since 2007", statLabel: "operating in Mandaluyong for nearly two decades" },
    opening: "This firm has been operating in Mandaluyong since 2007 — nearly two decades of real, ongoing work.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "An older Google Business Site is the closest thing to a standalone site" },
    findings: [
      "A Facebook page",
      "An older Google Business Site",
      "A name close enough to a generic search phrase that it's hard to search for directly",
      "No confirmed named CPA or owner shown anywhere public",
    ],
    problem: "Business owners are being asked to trust this firm with tax filings and financial records, but there's no clear, current page anywhere that confirms exactly who they'd be working with — even the firm's own name is close enough to a generic phrase that it's hard to search for directly.",
    fixes: [
      "Services clearly explained",
      "A confirmed Mandaluyong office address",
      "A direct, professional way to get in touch",
      "A single identity, separate from generic search results",
    ],
    closing: "Almost two decades in business deserves a website that's easier to find than the firm's own name is to search for.",
  },
  "judy-cabrera-accounting-concept": {
    name: "Judy M. Cabrera Accounting Services",
    conceptPath: "/judy-cabrera-accounting-concept",
    hero: "/accounting/judy-cabrera-accounting-concept/community-seminar.jpg",
    risk: "You're a small-business owner in Naic weighing who handles your bookkeeping, payroll and BIR filings. Before committing, you'd want to know Judy Montano-Cabrera is a real, named CPA — not just a Facebook page — and see the community work she's actually done, like the business-registration seminars she's run.",
    reputation: { rating: "5.0", reviewCount: null },
    opening: "Judy M. Cabrera Accounting Services already has something a website cannot manufacture: a real, named CPA with an active local presence and real community work behind her.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "A LinkedIn profile for Judy Montano-Cabrera, CPA" },
    findings: [
      "An active Facebook page",
      "A LinkedIn profile for Judy Montano-Cabrera, CPA",
      "5.0★ local-directory rating",
      "No standalone website",
    ],
    problem: "Judy already has a named, credentialed identity online — a real CPA with a LinkedIn profile and an active Facebook page. What's missing is one place that brings her services, her credentials and the community work she's actually done, like her business-registration seminars, together in one professional page.",
    fixes: [
      "Her CPA credentials clearly shown",
      "The seminars and community work she's actually done",
      "Services laid out for small-business owners specifically",
      "A direct, simple way to reach her",
    ],
    closing: "Facebook already brought people to Judy. A website gives that credibility a permanent, professional home.",
  },
  "talaba-accounting-cebu-concept": {
    name: "R. Talaba Accounting Firm",
    conceptPath: "/talaba-accounting-cebu-concept",
    hero: null,
    risk: "You need an accountant in Mandaue for tax compliance, SEC registration or a business loan. Before reaching out, you'd want to know that R. Talaba is a real, licensed CPA — not just an initial and a phone number.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "This Mandaue firm already has a real, positive local reputation — 5.0★ across its public reviews.",
    presence: { facebook: "weak", instagram: "none", website: "none", extra: "Facebook is referenced by directories, but the exact page is hard to find directly" },
    findings: [
      "A Facebook presence referenced by directories, though not easy to find directly",
      "5.0★ across 5+ public reviews",
      "No standalone website",
      "No page clearly confirming who's behind \"R. Talaba\"",
    ],
    problem: "\"R. Talaba\" doesn't tell a prospective client much on its own. Before trusting the firm with tax compliance or a business registration, they'd want to confirm there's a real, licensed CPA behind the name — something nothing public currently makes easy.",
    fixes: [
      "Clear confirmation of the firm's licensed CPA status",
      "Services laid out plainly — tax, bookkeeping, SEC registration, permits",
      "A confirmed Mandaue office address",
      "A direct way to get in touch",
    ],
    closing: "A name alone doesn't build trust. A page that shows the license, the services and the office does.",
  },
  "hico-janda-cpas-concept": {
    name: "Hico, Janda & Co., CPAs",
    conceptPath: "/hico-janda-cpas-concept",
    hero: null,
    risk: "You're trusting a Laguna firm with your taxes, audits or financial statements. Before reaching out, you'd want to know that a CPA with real government-accounting and academe experience is actually behind it — not just a name on a directory listing.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "This Laguna firm has a founding CPA with a real, verifiable background — government accounting experience, academe, and a legitimate practice license.",
    presence: { facebook: "none", instagram: "none", website: "none", extra: "Older material online refers to a differently-named predecessor firm" },
    findings: [
      "5.0★ across roughly 5 public reviews",
      "No verified Facebook page",
      "No Instagram account",
      "No independent website",
      "Older material online refers to a differently-named predecessor firm",
    ],
    problem: "Someone researching this Laguna firm today finds almost nothing current — no Facebook, no website — and what little exists online is tied to an older, differently-named predecessor. There's no clear, current page confirming who's actually running the firm now.",
    fixes: [
      "The founding CPA's real, verifiable background",
      "Services explained — audit, tax, financial advisory, bookkeeping",
      "A confirmed Laguna office address",
      "A clear, current identity separate from the older predecessor material",
    ],
    closing: "Right now, that background isn't visible anywhere online. A website is the simplest way to put it in front of the businesses that need it.",
  },
  "pso-accounting-group-concept": {
    name: "PSO Accounting Group",
    conceptPath: "/pso-accounting-group-concept",
    hero: null,
    risk: "You're comparing outsourced accounting firms across Makati, BGC and Alabang. Before signing on, you'd want to know that Sherwin Porciuncula, CPA, built this practice from scratch — not just land on whichever of PSO's several near-identical branch websites happens to rank first.",
    reputation: { rating: "4.8", reviewCount: 6 },
    opening: "PSO Accounting Group already has real visibility — a founder-led practice with multiple offices and a genuine track record since 2015.",
    presence: { facebook: "good", instagram: "none", website: "fragmented", extra: "Several near-identical branch websites — pso-accounting.com, makatiaccountants.com, bgc-accountants.com and others" },
    findings: [
      "An active Facebook page",
      "A LinkedIn company page",
      "Several different branch websites — pso-accounting.com, makatiaccountants.com, bgc-accountants.com and others",
      "6+ detailed public reviews from both clients and staff",
    ],
    problem: "PSO's problem isn't a lack of information — it's too many versions of it. Several near-identical branch websites make it hard for a prospective client to know which office, which contact number and which information is actually current.",
    fixes: [
      "One brand, presented consistently across every location",
      "All branches — Makati, Alabang, BGC — on one authoritative page",
      "Sherwin Porciuncula's real founder story",
      "Services and client reviews in one place, not spread across domains",
    ],
    closing: "More websites haven't made PSO easier to trust. One authoritative site would.",
  },

  // Phase 2 — Real Estate (5). Sourced entirely from realestate/realEstateAgents.js,
  // which already includes a research-verified "websiteAssessment" per agent — used
  // directly here rather than re-derived.
  "luchelle-ann-cabigon-real-estate-concept": {
    name: "Luchelle Ann Cabigon",
    conceptPath: "/luchelle-ann-cabigon-real-estate-concept",
    hero: "/realestate/luchelle-ann-cabigon/486813947-1094896649323337-1096287870758457284-n.webp",
    risk: "You're about to trust a broker with a Cebu property purchase or lease — real money, often decided from overseas.",
    reputation: { rating: "5.0", reviewCount: 9 },
    opening: "Luchelle Ann Cabigon already has something a website cannot manufacture: 14 years practicing since 2011, a 5.0★ rating across 9 Rentpad reviews, and a publicly listed PRC license.",
    presence: { facebook: "good", instagram: "none", website: "weak", extra: "cebuhomepages.com exists, but still shows dated registration details, and its listings subdomain has template/demo content and 2019-era credentials" },
    findings: [
      "Active Facebook/Messenger",
      "5.0★ across 9 Rentpad reviews",
      "PRC license no. 0006014 listed publicly",
      "An existing website (cebuhomepages.com) with dated registration info and a listings subdomain showing template/demo content",
    ],
    problem: "Overseas buyers and investors — Luchelle's core clients — check a broker's site carefully before wiring money across borders, but cebuhomepages.com currently shows 2019-era credentials and placeholder listing content, which undercuts exactly the trust an overseas buyer is looking to confirm.",
    fixes: [
      "Current PRC credentials and the 2011 practicing history stated accurately",
      "Real, current Cebu listings instead of template/demo content",
      "First-time buyer, OFW and overseas-investor guidance explained clearly",
      "The property-management service for owners holding property remotely",
    ],
    closing: "14 years and a 5.0★ record are real. The website should confirm that, not raise questions with outdated content.",
  },
  "nikki-dela-pena-real-estate-concept": {
    name: "Nikki Dela Peña",
    conceptPath: "/nikki-dela-pena-real-estate-concept",
    hero: "/realestate/nikki-dela-pena/capture-070413.webp",
    risk: "You're about to trust a broker with a Makati or BGC condo search — real money, decided quickly in a competitive market.",
    reputation: { rating: "4.9", reviewCount: 7 },
    opening: "Nikki Dela Peña already has something a website cannot manufacture: a nurse-turned-realtor story, her own company (DealSeeker.PH, founded 2019), and a 4.9★ rating across 7 Rentpad reviews.",
    presence: { facebook: "none", instagram: "none", website: "weak", extra: "Nikki has a profile page within DealSeeker.PH rather than a site of her own, and several listed properties on it are a few years old" },
    findings: [
      "A profile within DealSeeker.PH, not an independent personal site",
      "4.9★ across 7 Rentpad reviews",
      "Founded DealSeeker.PH in Makati in 2019",
      "Several visible listings on her current profile are several years old",
    ],
    problem: "Nikki founded her own company, but the page representing her is a profile inside that company's site rather than her own current, personally-branded page — and some of the listings visitors currently see are years out of date.",
    fixes: [
      "Her nurse-to-realtor story told as her own, not buried in a company profile",
      "Current Makati and BGC listings, not several-years-old entries",
      "Legazpi Village, Salcedo Village, Rockwell and Fort Bonifacio coverage stated clearly",
      "A direct way to start a condo search conversation",
    ],
    closing: "DealSeeker.PH is Nikki's own company. Her own page should look like it, not like an older listing left over from a previous year.",
  },
  "derrick-dumaya-real-estate-concept": {
    name: "Derrick Dumaya",
    conceptPath: "/derrick-dumaya-real-estate-concept",
    hero: "/realestate/derrick-dumaya/527066861-1172222528260366-1328241633913822182-n.webp",
    risk: "You're about to trust a broker with an Ortigas-area resale or rental — real money, often coordinated remotely.",
    reputation: { rating: "4.9", reviewCount: 12 },
    opening: "Derrick Dumaya already has something a website cannot manufacture: a PRC broker license, 12 Rentpad reviews at 4.9★, and repeated praise for support that continues after move-in.",
    presence: { facebook: "none", instagram: "none", website: "weak", extra: "A basic Wix site exists (dhdumaya.wixsite.com), alongside separate LinkedIn, OnePropertee, Carousell and Rentpad profiles — no single owned destination brings them together" },
    findings: [
      "A basic Wix site, plus separate LinkedIn, OnePropertee, Carousell and Rentpad profiles",
      "4.9★ across 12 Rentpad reviews",
      "PRC broker no. 33533",
      "Reviews specifically praise support that continues after the lease or sale closes",
    ],
    problem: "A client comparing brokers has to check five different places — a Wix site, LinkedIn, OnePropertee, Carousell and Rentpad — to get the full picture of Derrick's listings and reputation, with no single page bringing it together.",
    fixes: [
      "PRC license and Ortigas, Pasig, Mandaluyong and BGC coverage stated in one place",
      "The after-move support reviewers specifically praise, presented as Derrick's own service",
      "Current resale and leasing listings organized clearly",
      "One destination instead of five separate profiles",
    ],
    closing: "12 strong reviews already describe reliable follow-through. One real website replaces five scattered profiles with a single place that proves it.",
  },
  "kaye-arellano-real-estate-concept": {
    name: "Kaye Arellano",
    conceptPath: "/kaye-arellano-real-estate-concept",
    hero: "/realestate/kaye-arellano/capture-072944.webp",
    risk: "You're about to trust a broker with a fast-moving Makati or BGC corporate relocation — real money, on a tight timeline.",
    reputation: { rating: "5.0", reviewCount: 8 },
    opening: "Kaye Arellano already has something a website cannot manufacture: a perfect 5.0★ rating across 8 Rentpad reviews, with clients specifically praising her responsiveness on tight corporate timelines.",
    presence: { facebook: "none", instagram: "none", website: "none", extra: "No dedicated business website was found — her visible presence is limited to marketplace profile and listing pages (Rentpad, Dot Property)" },
    findings: [
      "No verified Facebook or Instagram presence",
      "No independent website — only marketplace profile pages (Rentpad, Dot Property)",
      "5.0★ across 8 Rentpad reviews",
      "Reviews specifically describe fast responses and support for corporate and international relocations",
    ],
    problem: "Corporate relocation clients need to move fast and trust quickly — but right now, confirming Kaye is a real, responsive broker means digging through marketplace listing pages rather than finding one clear, professional page built for exactly that kind of client.",
    fixes: [
      "Corporate relocation and international-resident service shown as her specific focus",
      "Makati and BGC leasing inventory presented clearly",
      "The fast, responsive service reviewers specifically describe",
      "A direct way to start a time-sensitive relocation request",
    ],
    closing: "8 clients already describe fast, dependable service. A website is the one place that makes her that easy to find before the next tight-timeline move.",
  },
  "john-paul-silva-real-estate-concept": {
    name: "John Paul Silva",
    conceptPath: "/john-paul-silva-real-estate-concept",
    hero: "/realestate/john-paul-silva/474639943-488586294275797-1895976805545043432-n.webp",
    risk: "You're about to trust a broker with a Metro Manila home search — real money, often coordinated from overseas.",
    reputation: { rating: "5.0", reviewCount: 5 },
    opening: "John Paul Silva already has something a website cannot manufacture: a 5.0★ rating across 5 Rentpad reviews, with clients specifically describing help that continued well past move-in.",
    presence: { facebook: "none", instagram: "none", website: "none", extra: "No dedicated business website was found — clients currently piece his story together from LinkedIn and Rentpad" },
    findings: [
      "No verified Facebook or Instagram presence",
      "No independent website — clients rely on LinkedIn and Rentpad to piece together his services",
      "5.0★ across 5 Rentpad reviews",
      "Reviews specifically mention help extending beyond the lease, including practical setup tasks",
    ],
    problem: "Overseas clients coordinating a move need to trust a broker before they've even met — but John Paul's actual story, spanning premium condos, houses and property-management support, only exists split across LinkedIn and a marketplace profile page.",
    fixes: [
      "Condominium leasing, larger homes and property-management support shown as distinct services",
      "Remote-search support for overseas clients explained clearly",
      "The beyond-the-lease help reviewers specifically describe",
      "A direct way to start a search from overseas",
    ],
    closing: "5 clients already describe support that went beyond the job. A website is where that reputation finally becomes his own.",
  },
};

export const DEFAULT_HOOK = {
  name: "your business",
  conceptPath: "/ph",
  hero: null,
  risk: "You're about to trust a business with real money, based only on what you can find about them online.",
  reputation: { rating: null, reviewCount: null, stat: "Your reputation", statLabel: "already exists — it just needs one place to live" },
};

const DENTAL_HERO_FALLBACK = {
  "21-dental-clinic-concept": "/dental/21-dental-clinic-concept/hero.jpg",
  "baroquina-dental-concept": "/dental/baroquina-dental-concept/team.jpg",
  "beyond-smile-dental-concept": "/dental/beyond-smile-dental-concept/logo.jpg",
  "blacano-dental-concept": "/dental/blacano-dental-concept/doctor.jpg",
  "cf-dental-concept": "/dental/cf-dental-concept/signage.jpg",
  "cedro-dental-concept": "/dental/cedro-dental-concept/whitening.jpg",
  "city-smile-dental-concept": "/dental/city-smile-dental-concept/logo.jpg",
  "family-tree-dental-concept": "/dental/family-tree-dental-concept/interior.jpg",
  "gallinero-dental-concept": "/dental/gallinero-dental-concept/sign-close.jpg",
  "ong-dental-concept": "/dental/ong-dental-concept/logo.jpg",
  "orevillos-dental-concept": "/dental/orevillos-dental-concept/logo.jpg",
  "smiles-dental-concept": "/dental/smiles-dental-concept/promo.png",
  "st-michaels-dental-concept": "/dental/st-michaels-dental-concept/before-after.jpg",
  "tooth-doctors-dental-concept": "/dental/tooth-doctors-dental-concept/hero.jpg",
};

export function registerDentalHooks(clinics) {
  Object.keys(clinics).forEach((slug) => {
    const c = clinics[slug];
    const reputation = c.rating
      ? { rating: c.rating.value, reviewCount: c.rating.count }
      : { rating: null, reviewCount: null, stat: c.qualitativeReputation || "A clinic with a real, active patient base", statLabel: "shown here from public listings" };
    BUSINESS_HOOKS[slug] = {
      name: c.name,
      conceptPath: `/${slug}`,
      hero: DENTAL_HERO_FALLBACK[slug] || null,
      risk: "You're about to let someone treat your teeth. Before booking, you want to know who the dentist is, what they treat, where the clinic is, what patients say, and how to make an appointment.",
      reputation,
    };
  });
}

export function getBusinessHook(slug) {
  return BUSINESS_HOOKS[slug] || DEFAULT_HOOK;
}

// Layers business-specific opening/presence/findings/problem/fixes/closing on top of the
// generic base entry a registration loop already created (name/conceptPath/hero/reputation
// stay exactly as that loop derived them — this only adds the personalized fields, matching
// the schema used for the 14 law/accounting entries above).
function applyPersonalization(map) {
  Object.entries(map).forEach(([slug, extra]) => {
    if (BUSINESS_HOOKS[slug]) Object.assign(BUSINESS_HOOKS[slug], extra);
  });
}

// Phase 2 — Dental (14). Sourced entirely from dental/dentalClinics.js (doctor names,
// Facebook/Instagram, ratings, branches, and real patient-review themes already collected
// there) — no new research performed for this batch.
const DENTAL_PERSONALIZATION = {
  "21-dental-clinic-concept": {
    opening: "21 Dental Clinic already has something a website cannot manufacture: patients who recommend it — 100% of the 5 public Facebook reviews recommend the clinic.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "\"21 Dental Clinic\" is a fairly generic name for a Dumaguete clinic" },
    findings: [
      "A Facebook page with a 100% recommend rate from 5 reviews",
      "No Instagram account",
      "No independent website",
      "A clinic name generic enough that search results could easily surface an unrelated \"21\"-named business",
    ],
    problem: "Dr. Cunanan's patients recommend the clinic on Facebook, but the name \"21 Dental Clinic\" doesn't stand out in a search the way a named dentist or a distinctive brand would — nothing online confirms this is specifically the Twin Arcade Building clinic in Dumaguete before a patient calls.",
    fixes: [
      "Dr. Jonathan W. Cunanan's name and services front and center",
      "The exact Twin Arcade Building location in Dumaguete",
      "General, cosmetic and orthodontic services listed clearly",
      "A direct way to confirm this is the right clinic before visiting",
    ],
    closing: "Facebook is already working for 21 Dental Clinic. A named, located website makes it obvious which \"21 Dental Clinic\" a patient actually found.",
  },
  "baroquina-dental-concept": {
    opening: "Baroquiña Dental Clinic already has something a website cannot manufacture: a 4.8★ reputation across 42 reviews, an active Facebook and Instagram following, and two working Bacolod branches.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "Booking runs through a third-party page (SeriousMD), not a clinic-owned site. The specific patient praise for gentle, clearly-explained treatment names Dra. Luwela (Banaylo), an associate dentist at the clinic, rather than Dra. Baroquiña herself." },
    findings: [
      "Active Facebook and Instagram",
      "4.8★ across 42 reviews",
      "Two branches — Rosario and Lacson, Bacolod City",
      "Booking handled through a third-party page (SeriousMD)",
      "No independent, clinic-controlled website",
    ],
    problem: "Patients already describe the clinic's treatment as gentle and clearly explained — real themes across multiple reviews, not just stars, with several reviews specifically naming Dra. Luwela, an associate dentist at the clinic — but that reputation, both branches, and booking are split across Facebook, Instagram and a third-party booking page instead of one place the clinic actually controls.",
    fixes: [
      "Both branches — Rosario and Lacson — clearly listed with their own details",
      "Dra. Baroquiña and the clinic's dentists introduced by name",
      "The gentle, clearly-explained-treatment reputation patients already describe",
      "One clear path to book, instead of three separate platforms",
    ],
    closing: "Facebook and Instagram can keep doing what they already do well. A website makes both branches and the booking path easy to find in one place.",
  },
  "beyond-smile-dental-concept": {
    opening: "Beyond Smile Dental already has something a website cannot manufacture: a perfect 5.0★ rating across 22 reviews and two independent Reddit recommendations from Dumaguete locals.",
    presence: { facebook: "weak", instagram: "none", website: "none", extra: "The Facebook page found is Dr. Rodriguez Tehrani's personal profile, not a dedicated clinic page — and the name \"Beyond Smile Dental\" is also used by an unrelated clinic in Bangkok" },
    findings: [
      "5.0★ across 22 reviews",
      "Two independent Reddit recommendations naming the Central Arcade location specifically",
      "The Facebook presence found is a personal profile, not a business page",
      "No Instagram account",
      "No independent website — and the clinic's name is shared by an unrelated Bangkok clinic online",
    ],
    problem: "Locals already point each other to \"Beyond Smile at Central Arcade\" specifically — because searching \"Beyond Smile Dental\" on its own risks surfacing an unrelated clinic on the other side of the world, and there's no dedicated business page to settle which one is real.",
    fixes: [
      "Dr. Samira Sadat Rodriguez Tehrani's name and the Central Arcade address, unmistakably",
      "Services — general, cosmetic, orthodontic and restorative care",
      "Real digital X-ray and veneer results already shared",
      "A clear identity separate from the unrelated same-named clinic abroad",
    ],
    closing: "The 5.0★ rating and the Reddit recommendations are real. What's missing is a page that makes clear which \"Beyond Smile\" they're actually about.",
  },
  "blacano-dental-concept": {
    opening: "Blacano Dental Center already has something a website cannot manufacture: a 5.0★ rating across 46 reviews and a 100% Facebook recommend rate from 23 more.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "5.0★ across 46 reviews, plus a 100% Facebook recommend rate from 23 reviews",
      "Patients specifically describe Dr. Kyra as gentle, clear, and following up personally after procedures",
      "No independent website",
    ],
    problem: "Dr. Kyra's patients describe a specific, consistent experience — a root canal that saved a tooth another dentist wanted to extract, a gentle hand with TAD-assisted orthodontic cases, personal follow-up by SMS — but that level of detail only exists scattered across individual Facebook and Google reviews, not anywhere the clinic itself controls.",
    fixes: [
      "Dr. Kyra Jean Blacano introduced by name, with the orthodontic/TAD work she's known for",
      "The La Paz, Iloilo City location",
      "Real case photos already shared — braces with TADs, aesthetic restorations",
      "The specific care patients already describe, presented as the clinic's own story",
    ],
    closing: "Facebook and Instagram are already working for Blacano Dental. A website gives that reputation — and Dr. Kyra's name — a permanent home.",
  },
  "cf-dental-concept": {
    opening: "C & F Dental Care already has something a website cannot manufacture: a 100% Facebook recommend rate from 205 reviews, and two named doctors running the practice together.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "4.7★ across 53 reviews, plus a 100% Facebook recommend rate from 205 reviews",
      "Two named doctors — Dr. Chariz T. Asoy (CEO) and Dr. Felix Trinidad Asoy (COO, Oral Surgery)",
      "No independent website",
    ],
    problem: "205 Facebook recommendations is an unusually large number for a single clinic to have built — but nothing online explains, in one place, that C & F is run by two named dentists with distinct roles, or shows the Myobrace and oral-surgery work patients specifically mention.",
    fixes: [
      "Dr. Chariz and Dr. Felix Asoy introduced by name and role",
      "Myobrace, general, cosmetic and oral surgery services listed clearly",
      "The Capitol Site, Cebu City location",
      "Real patient themes already gathered — clear explanations, fast and painless procedures",
    ],
    closing: "205 people have already recommended C & F on Facebook. A website turns that into the clinic's own first impression, not just a number on someone else's platform.",
  },
  "cedro-dental-concept": {
    opening: "Cedro Dental Clinic already has something a website cannot manufacture: a 5.0★ rating across 107 reviews — one of the strongest reputations in this entire batch.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "Booking runs through a third-party page (mydmdapp.com)" },
    findings: [
      "Active Facebook and Instagram",
      "5.0★ across 107 reviews, plus a 100% Facebook recommend rate from 40 reviews",
      "Multiple Reddit threads specifically praising Dr. Diane's gentle, painless extractions",
      "Booking handled through a third-party page, not a clinic-owned site",
      "No independent website",
    ],
    problem: "107 reviews and repeated, specific Reddit praise for Dr. Diane's gentle technique is a rare level of proof — but it's spread across Facebook, Instagram, Reddit and a third-party booking tool, with nothing that's actually Cedro Dental's own page bringing it together.",
    fixes: [
      "Dr. Gerzell Diane Cedro introduced by name",
      "Wisdom tooth extraction, whitening, braces and veneer services listed clearly",
      "The Bacolod City location and posted hours (Monday–Sunday, 9am–5pm)",
      "One clear booking path instead of a separate third-party app",
    ],
    closing: "107 reviews already say Cedro Dental does excellent work. That reputation deserves a home the clinic actually owns.",
  },
  "city-smile-dental-concept": {
    opening: "City Smile Dental & Associates already has something a website cannot manufacture: a 4.7★ rating across 49 reviews and real Reddit recommendations from Dumaguete patients.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "\"City Smile Dental\" is a name shared by other, unrelated clinics" },
    findings: [
      "Active Facebook and Instagram",
      "4.7★ across 49 reviews",
      "Reddit threads specifically recommending its braces, whitening and implant work",
      "A clinic name shared by other, unrelated \"City Smile\"-branded dental businesses",
      "No independent website",
    ],
    problem: "Patients already recommend City Smile Dental by name on Reddit, but \"City Smile Dental\" is common enough as a clinic name that a search doesn't reliably lead a new patient to this specific Daro, Dumaguete location versus an unrelated clinic using a similar name.",
    fixes: [
      "The exact Daro, Dumaguete location, unmistakably",
      "Braces, whitening, implant and general dentistry services listed clearly",
      "Real implant-planning and lab-work photos already shared",
      "A distinct identity separate from other similarly-named clinics",
    ],
    closing: "The reviews and the Reddit recommendations are real. A website makes sure they lead to the right City Smile.",
  },
  "family-tree-dental-concept": {
    opening: "Family Tree Dental Care already has something a website cannot manufacture: a husband-and-wife dentist team who've run the clinic since 2016, with real patient stories on Reddit and Facebook.",
    presence: { facebook: "good", instagram: "none", website: "none", extra: "\"Family Tree Dental Care\" is a fairly generic clinic name" },
    findings: [
      "An active Facebook page",
      "4.2★ across 22 reviews",
      "Operating since 2016 under Dr. Trisha Angelique T. Sagun and Dr. Ronald C. Sagun",
      "No Instagram account",
      "No independent website — and the clinic's name is generic enough to be confused with other \"family\"-branded clinics",
    ],
    problem: "Multiple Reddit patients specifically describe Family Tree as clean, calming and clearly explained — one even says the ambiance makes people look forward to check-ups — but a search for a generic name like \"Family Tree Dental\" doesn't reliably surface this specific Dumaguete clinic over an unrelated one.",
    fixes: [
      "Dr. Trisha and Dr. Ronald Sagun introduced by name, since 2016",
      "Family, orthodontic, extraction and root-canal services listed clearly",
      "The Sta. Catalina St., Dumaguete address, unmistakably",
      "The calm, welcoming atmosphere patients already describe",
    ],
    closing: "Since 2016 is a real track record. A named, located website makes sure a search for \"Family Tree\" actually leads here.",
  },
  "gallinero-dental-concept": {
    opening: "Gallinero Dental Center is already easy to find. The clinic has active Facebook and Instagram channels and 234 public reviews at 4.9★ — this isn't a case of needing a website because nobody knows the clinic exists.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "4.9★ across 234 reviews — one of the largest review counts in this entire research batch",
      "Two branches — Jibao-an, Pavia (main) and a second Iloilo branch",
      "Run by Dr. Jerah Leen Mai V. Gallinero and Dr. Judah V. Gallinero",
      "No independent website",
    ],
    problem: "The clinic's online reputation has grown large enough that it deserves a permanent home of its own — right now, comparing both Gallinero doctors, both branches, and the treatments patients specifically praise still means piecing it together from Facebook posts, Instagram reels and individual reviews.",
    fixes: [
      "Both doctors — Dr. Jerah and Dr. Judah Gallinero — and both branches clearly laid out",
      "General, family and cosmetic dentistry services in one place",
      "The gentle, compassionate care 234 reviews already describe",
      "One location-comparison page instead of two branches spread across social posts",
    ],
    closing: "Social media should continue doing what it already does well. The website becomes the place those channels point to when someone is ready to seriously evaluate the clinic.",
  },
  "ong-dental-concept": {
    opening: "Ong Dental Clinic already has something a website cannot manufacture: multiple working Dumaguete locations, a specialization few clinics on Negros Island can claim, and years of public listings under Dr. Arnold Ong's name.",
    presence: { facebook: "fragmented", instagram: "none", website: "none", extra: "Two active Facebook pages exist — one per branch, not a duplicate — and \"Ong Dental Clinic\" is a common surname-based name. Note: the clinic's second branch is publicly referenced as being on Pinili St. in current sources, while it was recorded elsewhere as \"Daro\" — this should be reconfirmed directly with the clinic before publishing an exact branch address." },
    findings: [
      "Two active Facebook pages — one per branch, rather than a duplicate for the same location",
      "No Instagram account",
      "No independent website",
      "No structured or recent review count — an established local reputation, but not one with a clear public rating to point to",
      "A specialization publicly described as unique on Negros Island (orthodontics and prosthodontics)",
      "A common surname-based clinic name that could be confused with other \"Ong\"-named dental practices",
      "The exact second-branch address needs reconfirming — recorded in different sources as either Daro or Pinili St.",
    ],
    problem: "Two branches means two separate Facebook pages for a patient to choose between, with no single page explaining which branch is closer or confirming the clinic's specialization — something few other Negros Island clinics can claim.",
    fixes: [
      "Dr. Arnold Ong and both branches shown together, once the second branch's current address is reconfirmed",
      "The orthodontics/prosthodontics specialization stated clearly",
      "General dentistry, restorations and extraction services listed clearly",
      "One current, authoritative identity instead of two branch-specific Facebook pages",
    ],
    closing: "Multiple locations and a rare specialization are real assets. One website makes it clear which branch a patient should visit.",
  },
  "orevillos-dental-concept": {
    opening: "Orevillo's Dental Clinic already has something a website cannot manufacture: 18 years serving Dumaguete and Siquijor, since 2008.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "Operating since 2008 — 18 years in Dumaguete and Siquijor",
      "Two locations — Evermall, Dumaguete and a Siquijor branch",
      "A notable independent mention: a filling from Orevillo's was later praised by a US dentist",
      "No Instagram account and no independent website",
    ],
    problem: "18 years and two locations, including a Siquijor branch few Dumaguete clinics have, is a real differentiator — but there's no current public rating or review count to point to, and nothing that explains both locations and that history in one place.",
    fixes: [
      "Both locations — Evermall Dumaguete and Siquijor — clearly explained",
      "The clinic's history since 2008",
      "General, cosmetic, veneer and crown services listed clearly",
      "A clear, current way to reach either branch",
    ],
    closing: "18 years and a rare Siquijor branch are real advantages most clinics don't have. A website is the simplest way to make that history visible.",
  },
  "smiles-dental-concept": {
    opening: "Smiles Dental Care already has something a website cannot manufacture: an active Facebook and Instagram following and a clinic history dating to 2008.",
    presence: { facebook: "good", instagram: "good", website: "none", extra: "\"Smiles Dental Care\" is a name shared by other, unrelated dental businesses, and there's a related \"Focus Dental\" branch under the same group; a separate, much larger \"Smiles Dental Care Group PH\" page for the same lead doctor also exists and hasn't been fully reconciled with this page" },
    findings: [
      "Active Facebook and Instagram",
      "4.8★ across 12 reviews",
      "Operating since 2008, run by Dr. Seeress \"Mae\" Rubio and Dr. Seleena Lim",
      "A related second location, \"Focus Dental\" in Valencia, under the same Smiles Dental Care Group",
      "A separate, larger \"Smiles Dental Care Group PH\" Facebook page (49,000+ likes) and Instagram (@smilesdentalcaregroup.ph) also exist under Dr. Rubio's name — likely a newer or rebranded channel for the same practice, but not confirmed as the same page or the current primary one",
      "A clinic name shared by other, unrelated \"Smiles Dental\" businesses elsewhere",
      "No independent website",
    ],
    problem: "\"Smiles Dental\" is generic enough that other unrelated clinics use close variations of the same name, the connection between Smiles Dental Care and its sister Focus Dental location in Valencia isn't explained anywhere public, and it's now unclear to a visitor which of two differently-sized Facebook pages under Dr. Rubio's name is the clinic's current, primary channel.",
    fixes: [
      "Dr. Mae Rubio and Dr. Seleena Lim introduced by name",
      "Both locations — Smiles Dental Care and Focus Dental — explained as one group",
      "General dentistry and orthodontic services listed clearly",
      "A distinct identity separate from unrelated same-named clinics",
      "One clear, current channel to follow, instead of two differently-branded Facebook pages",
    ],
    closing: "Facebook and Instagram already work for Smiles Dental. A website makes sure a search for the name leads to the right clinic, the right branch — and the one channel that's actually current.",
  },
  "st-michaels-dental-concept": {
    opening: "St. Michael's Dental Care already has something a website cannot manufacture: strong ratings on two separate platforms — 4.7★ from 62 Google reviews and 4.9★ from WhatClinic — plus two working Iloilo branches.",
    presence: { facebook: "good", instagram: "good", website: "weak", extra: "A clinic-owned site, empireofsmile.com, was built with service pages and a client booking login and is still referenced by review and directory sites — but the domain currently fails to resolve (no live site as of this check), so it isn't a working destination today." },
    findings: [
      "Active Facebook and Instagram",
      "4.7★ across 62 reviews, plus a separate 4.9★ rating from 4 WhatClinic reviews",
      "Two branches — the main Gen. Luna Street location and a Molo branch",
      "Founded in 2016 by Dr. Jai and Dr. Bernie",
      "A past clinic-owned website (empireofsmile.com) turns up in search results, but its domain no longer resolves — this isn't a case of never having tried a website",
    ],
    problem: "Being well-reviewed on two different platforms (Google and WhatClinic) is unusual and a real strength, but the clinic's reputation is still split across Facebook, Instagram, Google and WhatClinic — and a previous website attempt (empireofsmile.com) is no longer live, leaving no single current page that brings it together.",
    fixes: [
      "Dr. Jai and Dr. Bernie, and both branches — Gen. Luna Street and Molo — clearly laid out",
      "Digital X-ray, veneers, implants, root canal and orthodontic services listed clearly",
      "The attentive, caring follow-up patients specifically describe",
      "One current page bringing together what's split across Google, WhatClinic and a lapsed previous website",
    ],
    closing: "Two platforms already rate St. Michael's highly, and the clinic has tried a website before. A new one gives that reputation a home that stays live.",
  },
  "tooth-doctors-dental-concept": {
    opening: "Tooth Doctors already has something a website cannot manufacture: a 4.8★ rating across 54 reviews and enough of a following that patients recommend it by name across Reddit, Facebook and Instagram.",
    presence: { facebook: "good", instagram: "weak", website: "none", extra: "Instagram activity exists (@toothdoctorsdavao is referenced by patients), but no officially verified Instagram account was confirmed. \"Tooth Doctors\" is also a fairly generic clinic name." },
    findings: [
      "An active Facebook page",
      "4.8★ across 54 reviews",
      "Patient mentions across Reddit, Facebook and an Instagram handle (@toothdoctorsdavao), though not all formally verified",
      "In-house X-ray, avoiding referrals elsewhere — specifically praised by patients",
      "A generic clinic name (\"Tooth Doctors\") that produces many unrelated results in a general search",
      "No independent website",
    ],
    problem: "Patients already praise fast Messenger replies and in-house X-ray capability, but \"Tooth Doctors\" alone doesn't distinguish this Matina, Davao City clinic from the many unrelated results the same generic name produces elsewhere.",
    fixes: [
      "The exact Matina, Davao City location, unmistakably (fronting Mapúa Malayan Colleges Mindanao)",
      "Cosmetic, orthodontic, general and oral surgery services listed clearly",
      "In-house X-ray and other real capabilities patients specifically mention",
      "A distinct identity separate from unrelated same-named results",
    ],
    closing: "54 reviews and fast Messenger replies already work in Tooth Doctors' favor. A named, located website makes sure a search actually lands here.",
  },
};

registerDentalHooks(CLINICS);
applyPersonalization(DENTAL_PERSONALIZATION);

EVENT_BUSINESSES.forEach((business) => {
  BUSINESS_HOOKS[business.slug] = {
    name: business.name,
    conceptPath: `/${business.slug}`,
    hero: business.media?.hero || null,
    risk: business.kind === "planner"
      ? "You're about to trust a team with the schedule, suppliers and decisions behind a once-in-a-lifetime celebration."
      : business.kind === "venue"
        ? "You're about to choose the place where your guests will gather, based on the clearest information you can find online."
        : "You're about to depend on event furniture, styling or party equipment arriving in the right quantity, condition and time window.",
    reputation: business.reviewSnapshot.match(/(\d\.\d)\//)
      ? { rating: business.reviewSnapshot.match(/(\d\.\d)\//)[1], reviewCount: Number(business.reviewSnapshot.match(/(?:from|about) (\d+)/)?.[1]) || null }
      : { rating: null, reviewCount: null, stat: "A real local track record", statLabel: "presented from the supplied research dossier" },
  };
});

// Phase 2 — Wedding & Events (10). Sourced entirely from events/eventBusinesses.js (contact
// info, review snapshots, named leads and the review themes already collected there).
const EVENTS_PERSONALIZATION = {
  "spotlight-events-ph-concept": {
    opening: "Spotlight Events PH already has something a website cannot manufacture: a 4.9★ rating and a founder, Aireen Mansueto, with a real hospitality career behind her — including banquet sales at Marco Polo Plaza Cebu — before founding Spotlight in 2014.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "4.9★ from 7 reviews",
      "Founded August 2014 by Aireen Mansueto",
      "No independent website",
    ],
    problem: "Aireen's hospitality background — banquet and hotel operations before founding Spotlight — is exactly the kind of credential a couple comparing planners would want to see clearly, but right now it only exists on her personal professional profile, not anywhere Spotlight's own page explains it.",
    fixes: [
      "Aireen Mansueto's hospitality background told as Spotlight's own story",
      "Full planning, partial planning and day-of coordination laid out as distinct services",
      "Wedding, corporate and social-event capability shown side by side",
      "A direct way to start a conversation about a real event date",
    ],
    closing: "Facebook and Instagram already bring people to Spotlight. A website is where Aireen's actual experience gets to do the convincing.",
  },
  "concepts-ideas-events-cebu-concept": {
    opening: "Concepts & Ideas Events Cebu already has something a website cannot manufacture: a couple who planned their Cebu wedding from 8,000 miles away and called the result beyond expectations.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "4.8★ from 12 reviews",
      "A named team — Helbert Arsenal Canonigo and Kelvin Jay Alvarado Olimba",
      "Real reviews specifically describing remote, overseas planning",
      "No independent website",
    ],
    problem: "Couples planning from overseas are exactly the clients most likely to search for proof before committing — but the story of a client planning an entire Cebu wedding from 8,000 miles away currently lives inside a review on a directory site, not anywhere Concepts & Ideas controls.",
    fixes: [
      "Helbert and Kelvin introduced as the team behind the work",
      "Wedding planning, styling and destination-wedding capability shown clearly",
      "The remote/overseas-planning strength turned into its own selling point",
      "A structured way for an overseas couple to start planning without a Facebook message",
    ],
    closing: "An overseas couple already trusted this team with their wedding from 8,000 miles away. A website makes that story easy for the next overseas couple to find.",
  },
  "the-centerpiece-cebu-concept": {
    opening: "The Centerpiece already has something a website cannot manufacture: a first-place ranking among Cebu wedding planners and coordinators in a 2025 peer-voted industry profile.",
    presence: { facebook: "none", instagram: "good", website: "none", extra: "\"The Centerpiece\" is a name shared by other, unrelated event and decor businesses" },
    findings: [
      "Active Instagram; no verified Facebook page",
      "Ranked first in a 2025 peer-voted Cebu wedding-industry profile",
      "Around 5.0★ from 7 reviews on a public directory",
      "Community discussion describes a generally well-organized, communicative team, alongside one note about slower group-chat replies from an assistant during a busy period",
      "A name shared by other, unrelated \"Centerpiece\"-branded event and decor businesses",
    ],
    problem: "Being ranked first among Cebu planners by peers is a real distinction, but \"The Centerpiece\" is generic enough that it's shared by unrelated decor and event businesses — a couple searching the name has no easy way to confirm which one earned that ranking.",
    fixes: [
      "Roderick Bautista and Eddie Jamin introduced as the directors behind the 2025 ranking",
      "Planning, budget tracking, design consulting and day-of direction laid out clearly",
      "A clear identity separate from unrelated similarly-named businesses",
      "A structured planning-process page instead of scattered Reddit threads",
    ],
    closing: "Peers already ranked this team first in Cebu. A website makes sure a search for \"The Centerpiece\" actually finds them.",
  },
  "historia-events-ph-concept": {
    opening: "Historia Events PH already has something a website cannot manufacture: eight years operating in Iloilo, since 2018.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "Publicly active since 2018",
      "Weddings, debuts, private celebrations and corporate events all handled",
      "No Instagram account and no independent website",
    ],
    problem: "Eight years in Iloilo across weddings, debuts and corporate events is a real range, but nothing outside Facebook explains that range in one place — a prospective client currently has to scroll posts to understand what Historia actually plans.",
    fixes: [
      "Mack LV introduced as the coordinator behind the work",
      "Weddings, debuts, private celebrations and corporate events shown as distinct services",
      "The eight-year Iloilo history told clearly",
      "A direct way to start planning a specific celebration",
    ],
    closing: "Eight years and a wide range of events is a real track record. A website is where that range finally gets explained in one place.",
  },
  "base-function-hall-concept": {
    opening: "Base Function Hall already has something a website cannot manufacture: a 4.6★ rating from roughly 53 reviews, and real praise for Therese Hall specifically.",
    presence: { facebook: "good", instagram: "good", website: "none" },
    findings: [
      "Active Facebook and Instagram",
      "4.6★ from about 53 reviews",
      "Therese Hall — capacity publicly stated up to 120 guests",
      "Reviews specifically praise the space, accommodating staff, and all-in packages",
      "No independent website",
    ],
    problem: "People choosing a function hall want to see the room and know the capacity before they call — reviews already describe Therese Hall as spacious and well-run, but that comes through only in scattered directory reviews, not from Base itself.",
    fixes: [
      "Therese Hall shown clearly, with its 120-guest capacity stated upfront",
      "Weddings, birthdays, seminars and community-event use laid out",
      "The all-in packages reviewers already praise, explained clearly",
      "A direct way to check availability instead of a Facebook message",
    ],
    closing: "53 reviews already describe a well-run hall. A website is where a couple or a family can actually picture the room before they call.",
  },
  "heart-star-events-place-concept": {
    opening: "Heart & Star Events Place already has something a website cannot manufacture: a poolside venue built specifically around family celebrations, with a 4.8★ rating from its public reviews.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "4.8★ rating on public listings",
      "The pool and outdoor space are the venue's central feature",
      "Baptisms, reunions, team-building and wedding receptions all hosted here",
      "No Instagram account and no independent website",
    ],
    problem: "A pool-centered venue is a specific, visual thing families want to see before booking — right now that means finding the right Facebook post, not a page built to actually show the space and what it's used for.",
    fixes: [
      "The pool and outdoor space shown as the venue's centerpiece",
      "Baptisms, reunions, team-building and family-day use laid out clearly",
      "Real photos of the space families are actually booking",
      "A direct way to ask about a date",
    ],
    closing: "Families already give this pool venue a 4.8★ rating. A website is where the next family gets to see it before they call.",
  },
  "yobhelles-meetings-banquets-concept": {
    opening: "Yobhelle's Meetings & Banquets already has something a website cannot manufacture: a real, established Toledo City venue built specifically for business meetings and catered occasions, not just weddings.",
    presence: { facebook: "weak", instagram: "none", website: "none", extra: "The venue's Facebook page displays as \"Yobhelle's Meetings and Banquets\" in its own page title, but its web address is still the older \"facebook.com/FourFoodGroups\" vanity URL — and public directories disagree on the current review count (roughly 4–7)" },
    findings: [
      "A Facebook page exists and is titled \"Yobhelle's Meetings and Banquets,\" but its web address is still the legacy \"facebook.com/FourFoodGroups\" URL",
      "Public directories disagree on the review count — reported as roughly 4 to 7 depending on the source",
      "No Instagram account and no independent website",
      "Positioned specifically for meetings, conferences and catered private events",
    ],
    problem: "A business meeting or conference booker searching \"Yobhelle's Meetings & Banquets\" and landing on a page whose web address still reads facebook.com/FourFoodGroups may hesitate, even though the page itself is correctly titled Yobhelle's — and the review count isn't consistent across the directories that do list it.",
    fixes: [
      "One page under a clean, current web address that matches the venue's actual name, \"Yobhelle's Meetings & Banquets\"",
      "Meetings, conferences and catered private events shown as the venue's actual focus",
      "A clear, current way to enquire about catering and audiovisual needs",
      "A single source of truth instead of conflicting directory listings",
    ],
    closing: "The venue itself is real and already hosts business events in Toledo. A website is what finally puts its own name on its own front door.",
  },
  "tc-rental-event-services-concept": {
    opening: "T&C Rental and Event Services already has something a website cannot manufacture: 13 years as the first Bohol- and Cebu-based furniture rental company, since 2012, with an active Instagram following of over 1,700 people.",
    presence: { facebook: "fragmented", instagram: "good", website: "none", extra: "Separate Facebook pages exist for Cebu (cebu.tables.chairs) and Bohol (bohol.tables.chairs) — two real, active pages for the same company's two service areas, not a discoverability gap" },
    findings: [
      "Active Instagram (@tncrental, 1,700+ followers)",
      "Two separate, active Facebook pages — one for Cebu, one for Bohol",
      "Publicly describes itself as the first Bohol & Cebu furniture rental company, established 2012",
      "Roughly 4.8–4.9★ from 10–11+ reviews across public sources",
      "A real, distinct furniture catalogue — Crossback Chairs, Louis Chairs, Cabriole Tables and more",
      "No independent website",
    ],
    problem: "13 years and a genuinely distinctive furniture catalogue are real strengths, but T&C's presence is split across an Instagram account and two separate, area-specific Facebook pages — a couple planning a Cebu or Bohol event has no single place to actually browse the catalogue before messaging one of two pages.",
    fixes: [
      "The real furniture catalogue — chairs, tables, wood and glass pieces — organized so it can actually be browsed",
      "Cebu and Bohol coverage shown together, from one company",
      "The 2012 founding and \"first in the region\" history told clearly",
      "A structured availability/quote request instead of choosing between two Facebook pages",
    ],
    closing: "13 years and 1,700+ Instagram followers are real proof. A website brings the Cebu and Bohol sides of the business together in one place instead of two.",
  },
  "jeffie-party-shop-concept": {
    opening: "Jeffie Party Shop already has something a website cannot manufacture: a 5.0★ rating across more than 100 public reviews — one of the strongest review counts in this entire research batch.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "5.0★ across 100+ reviews",
      "Public traces of the business dating back to at least 2017",
      "No Instagram account and no independent website",
    ],
    problem: "100+ five-star reviews is a rare and specific kind of proof, built up over years in Paco, Manila — but right now that reputation lives entirely on a platform Jeffie doesn't control, with nothing of its own explaining what it actually rents or how to get a quote.",
    fixes: [
      "Party and event rental categories shown clearly",
      "Christenings, birthdays and milestone-celebration packages laid out",
      "The years of history and the 100+ review reputation presented as Jeffie's own",
      "A direct quotation path instead of a Facebook message",
    ],
    closing: "100+ five-star reviews already prove Jeffie does this well. A website is where that reputation finally becomes Jeffie's own.",
  },
  "mav-party-needs-services-concept": {
    opening: "MAV Party Needs & Services already has something a website cannot manufacture: a founder, Marc Anjo Valdez, building the business since 2015, with named clients who specifically say he's kind and easy to work with.",
    presence: { facebook: "good", instagram: "none", website: "none" },
    findings: [
      "An active Facebook page",
      "Around 5.0★ from 10+ reviews, plus named testimonials",
      "Founded by Marc Anjo Valdez in 2015",
      "Balloon styling, table setup, photobooths and floral design all offered",
      "No Instagram account and no independent website",
    ],
    problem: "Named clients already describe specific things — flexible payment conversations, an energetic host, a birthday theme delivered exactly as requested — but that detail is scattered across individual testimonials on an older Wix site, not presented as MAV's own story.",
    fixes: [
      "Marc Anjo Valdez introduced as the founder, since 2015",
      "Balloon decoration, table setup, photobooth and floral services shown as distinct offerings",
      "The specific client themes already gathered — flexible, communicative, delivers the requested theme",
      "A direct way to start planning a birthday, wedding or corporate event",
    ],
    closing: "Named clients already vouch for MAV by name. A website is where that trust becomes the business's own first impression.",
  },
};

applyPersonalization(EVENTS_PERSONALIZATION);
